/**
 * API - Wrapper centralisé pour les appels Supabase
 * Adapté pour la structure WebEdena (prestations, employes, creneaux)
 */

import { supabase } from './supabaseClient.js';

function normalizeIdList(ids) {
  if (!Array.isArray(ids)) {
    return [];
  }

  return Array.from(
    new Set(ids.map((id) => String(id || '').trim()).filter(Boolean))
  );
}

/**
 * Récupère toutes les prestations actives d'un établissement
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} [siteId] - ID du site (optionnel)
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getPrestations(etablissementId, _siteId = null) {
  const includeInactive = arguments.length >= 3 && arguments[2]?.includeInactive === true;

  let query = supabase
    .from('prestations')
    .select(
      `
      *,
      categories:prestations_categories(
        categorie:categorie_id (id, libelle)
      ),
      competences:prestations_competences(
        competence:competence_id (id, code, libelle)
      )
    `
    )
    .eq('etablissement_id', etablissementId);

  if (!includeInactive) {
    query = query.eq('active', true);
  }

  query = query.order('nom');

  const { data, error } = await query;

  // Transformer pour compatibilité ascendante (certains composants attendent .categorie)
  const transformed = (data || []).map((p) => {
    const catObj = p.categories?.[0]?.categorie || null;
    return {
      ...p,
      categorie: catObj,
      // S'assurer que categorie_id est bien défini, soit depuis la colonne, soit depuis la relation
      categorie_id: p.categorie_id || catObj?.id || null,
    };
  });

  return { data: transformed, error };
}

/**
 * Récupère tous les produits actifs d'un établissement
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getProduits(etablissementId) {
  const query = supabase
    .from('produits')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .eq('actif', true)
    .order('nom');

  const { data, error } = await query;
  return { data: data || [], error };
}

export async function copyListeTarifs(fromListeId, toListeId) {
  const { data, error } = await supabase
    .from('listes_tarifs_prestations')
    .select('prestation_id, prix_centimes_public, prix_centimes_abonne')
    .eq('liste_id', fromListeId);
  if (error) return { data: null, error };
  const rows = (data || []).map((d) => ({
    liste_id: toListeId,
    prestation_id: d.prestation_id,
    prix_centimes_public: d.prix_centimes_public ?? null,
    prix_centimes_abonne: d.prix_centimes_abonne ?? null,
  }));
  if (rows.length === 0) return { data: [], error: null };
  const ins = await supabase
    .from('listes_tarifs_prestations')
    .insert(rows)
    .select();
  return { data: ins.data, error: ins.error };
}

/**
 * Récupère des clients par liste d'IDs (sans pagination)
 * @param {string[]} ids
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getClientsByIds(ids) {
  if (!ids || ids.length === 0) return { data: [], error: null };
  const unique = Array.from(new Set(ids.map(String)));
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .in('id', unique);
  return { data: data || [], error };
}

export async function getCreneauxPourPeriodeLite({ etablissementId, siteId, dateDebut, dateFin }) {
  try {
    if (!etablissementId || !siteId || !dateDebut || !dateFin) {
      return { data: [], error: null };
    }

    const start = new Date(String(dateDebut).slice(0, 10) + 'T00:00:00.000');
    const end = new Date(String(dateFin).slice(0, 10) + 'T23:59:59.999');

    const { data, error } = await supabase
      .from('creneaux')
      .select('id, debut, employe_id')
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .gte('debut', start.toISOString())
      .lte('debut', end.toISOString());

    return { data: data || [], error };
  } catch (error) {
    console.error('Erreur getCreneauxPourPeriodeLite:', error);
    return { data: null, error };
  }
}

export async function getPlanningBlocagesPublicsEmployesPourPeriode(
  etablissementId,
  siteId,
  dateDebut,
  dateFin,
) {
  if (!etablissementId || !siteId || !dateDebut || !dateFin) {
    return { data: [], error: null };
  }

  const start = String(dateDebut).slice(0, 10);
  const end = String(dateFin).slice(0, 10);

  const { data, error } = await supabase
    .from('planning_blocages_publics_employes')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .gte('date_jour', start)
    .lte('date_jour', end);

  return { data: data || [], error };
}

/**
 * Indique si un jour de planning a déjà des réservations ou des indisponibilités
 * @param {string} etablissementId
 * @param {string} siteId
 * @param {string} dateJour - 'YYYY-MM-DD'
 * @returns {Promise<{data: {hasReservations: boolean, hasIndisponibilitesEmployes: boolean, hasIndisponibilitesRessources: boolean}, error: object|null}>}
 */
export async function getPlanningJourLocks(etablissementId, siteId, dateJour) {
  if (!etablissementId || !siteId || !dateJour) {
    return {
      data: {
        hasReservations: false,
        hasIndisponibilitesEmployes: false,
        hasIndisponibilitesRessources: false,
      },
      error: null,
    };
  }

  const start = new Date(dateJour + 'T00:00:00');
  const end = new Date(dateJour + 'T23:59:59.999');
  const startIso = start.toISOString();
  const endIso = end.toISOString();

  const [resResa, resIndEmp, resIndRes] = await Promise.all([
    supabase
      .from('reservations')
      .select('id')
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .gte('debut', startIso)
      .lte('debut', endIso)
      .neq('statut', 'annulee')
      .limit(1),
    supabase
      .from('absences_employes')
      .select('id')
      .eq('etablissement_id', etablissementId)
      .lt('debut', endIso)
      .gte('fin', startIso)
      .limit(1),
    supabase
      .from('indisponibilites_ressources')
      .select('id')
      .eq('etablissement_id', etablissementId)
      .lt('debut', endIso)
      .gte('fin', startIso)
      .limit(1),
  ]);

  const error = resResa.error || resIndEmp.error || resIndRes.error || null;
  if (error) {
    return {
      data: {
        hasReservations: false,
        hasIndisponibilitesEmployes: false,
        hasIndisponibilitesRessources: false,
      },
      error,
    };
  }

  return {
    data: {
      hasReservations: Array.isArray(resResa.data) && resResa.data.length > 0,
      hasIndisponibilitesEmployes: Array.isArray(resIndEmp.data) && resIndEmp.data.length > 0,
      hasIndisponibilitesRessources: Array.isArray(resIndRes.data) && resIndRes.data.length > 0,
    },
    error: null,
  };
}

export async function hasReservationsForEmployeOnDay(etablissementId, siteId, employeId, dateJour) {
  if (!etablissementId || !siteId || !employeId || !dateJour) {
    return { data: false, error: null };
  }

  const start = new Date(dateJour + 'T00:00:00');
  const end = new Date(dateJour + 'T23:59:59.999');
  const startIso = start.toISOString();
  const endIso = end.toISOString();

  const { data, error } = await supabase
    .from('reservations')
    .select('id')
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .eq('employe_id', employeId)
    .gte('debut', startIso)
    .lte('debut', endIso)
    .neq('statut', 'annulee')
    .limit(1);

  if (error) {
    return { data: false, error };
  }

  return { data: Array.isArray(data) && data.length > 0, error: null };
}

export async function deletePlanningJourIfNoReservedCreneaux(etablissementId, siteId, dateJour) {
  if (!etablissementId || !siteId || !dateJour) {
    return { data: false, error: null };
  }

  const start = new Date(dateJour + 'T00:00:00');
  const end = new Date(dateJour + 'T23:59:59.999');
  const startIso = start.toISOString();
  const endIso = end.toISOString();

  const [resResa, resCreneauxReserves] = await Promise.all([
    supabase
      .from('reservations')
      .select('id')
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .gte('debut', startIso)
      .lte('debut', endIso)
      .neq('statut', 'annulee')
      .limit(1),
    supabase
      .from('creneaux')
      .select('id')
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .gte('debut', startIso)
      .lte('debut', endIso)
      .not('reservation_id', 'is', null)
      .limit(1),
  ]);

  const checkError = resResa.error || resCreneauxReserves.error || null;
  if (checkError) {
    return { data: false, error: checkError };
  }

  const hasReservations = Array.isArray(resResa.data) && resResa.data.length > 0;
  const hasCreneauxReserves =
    Array.isArray(resCreneauxReserves.data) && resCreneauxReserves.data.length > 0;

  if (hasReservations || hasCreneauxReserves) {
    return {
      data: false,
      error: {
        message: "Suppression impossible : au moins un créneau est déjà réservé pour cette journée.",
      },
    };
  }

  const [delCreneaux, delBlocages, delIndEmp, delIndRes, delJour] = await Promise.all([
    supabase
      .from('creneaux')
      .delete()
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .gte('debut', startIso)
      .lte('debut', endIso)
      .is('reservation_id', null),
    supabase
      .from('planning_blocages_publics_employes')
      .delete()
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .eq('date_jour', dateJour),
    supabase
      .from('absences_employes')
      .delete()
      .eq('etablissement_id', etablissementId)
      .gte('debut', startIso)
      .lte('fin', endIso),
    supabase
      .from('indisponibilites_ressources')
      .delete()
      .eq('etablissement_id', etablissementId)
      .gte('debut', startIso)
      .lte('fin', endIso),
    supabase
      .from('planning_jours')
      .delete()
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .eq('date_jour', dateJour),
  ]);

  const deleteError =
    delCreneaux.error || delBlocages.error || delIndEmp.error || delIndRes.error || delJour.error || null;
  if (deleteError) {
    return { data: false, error: deleteError };
  }

  return { data: true, error: null };
}

/**
 * Supprime tous les créneaux d'une journée pour un établissement et un site donnés
 * @param {string} etablissementId
 * @param {string} siteId
 * @param {string} dateJour - 'YYYY-MM-DD'
 * @returns {Promise<{error: object|null}>}
 */
export async function deleteCreneauxForJour(etablissementId, siteId, dateJour) {
  if (!etablissementId || !siteId || !dateJour) {
    return { error: null };
  }

  const start = new Date(dateJour + 'T00:00:00');
  const end = new Date(dateJour + 'T23:59:59.999');

  const { error } = await supabase
    .from('creneaux')
    .delete()
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .gte('debut', start.toISOString())
    .lte('debut', end.toISOString());

  return { error };
}

export async function deleteCreneauxForJourAndEmploye(etablissementId, siteId, employeId, dateJour) {
  if (!etablissementId || !siteId || !employeId || !dateJour) {
    return { error: null };
  }

  const start = new Date(dateJour + 'T00:00:00');
  const end = new Date(dateJour + 'T23:59:59.999');

  const { error } = await supabase
    .from('creneaux')
    .delete()
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .eq('employe_id', employeId)
    .gte('debut', start.toISOString())
    .lte('debut', end.toISOString());

  return { error };
}

export async function deleteIndisponibilitesEmployeForDay(etablissementId, employeId, dateJour) {
  if (!etablissementId || !employeId || !dateJour) {
    return { error: null };
  }

  const start = new Date(dateJour + 'T00:00:00');
  const end = new Date(dateJour + 'T23:59:59.999');
  const startIso = start.toISOString();
  const endIso = end.toISOString();

  // Supprimer toute indisponibilité qui CHEVAUCHE la journée :
  // debut < finJour ET fin > debutJour
  const { error } = await supabase
    .from('absences_employes')
    .delete()
    .eq('etablissement_id', etablissementId)
    .eq('employe_id', employeId)
    .lt('debut', endIso)
    .gt('fin', startIso);

  return { error };
}

/**
 * Libère une liste de créneaux en les repassant à statut 'libre'.
 * @param {string[]} creneauIds - Liste d'IDs de créneaux à libérer
 * @returns {Promise<{error: object|null}>}
 */
export async function libererCreneaux(creneauIds) {
  if (!Array.isArray(creneauIds) || creneauIds.length === 0) {
    return { error: null };
  }

  const { error } = await supabase
    .from('creneaux')
    .update({ statut: 'libre', reservation_id: null })
    .in('id', creneauIds);

  return { error };
}

/**
 * Récupère une prestation par ID
 * @param {string} id
 * @returns {Promise<{data: object, error: object}>}
 */
export async function getPrestationById(id) {
  const { data, error } = await supabase
    .from('prestations')
    .select(
      `
      *,
      categories:prestations_categories(
        categorie:categorie_id (id, libelle)
      ),
      competence:competence_id (id, code, libelle)
    `
    )
    .eq('id', id)
    .single();

  if (data) {
    data.categorie = data.categories?.[0]?.categorie || null;
  }

  return { data, error };
}

/**
 * Récupère tous les employés actifs d'un site
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} siteId - ID du site
 * @param {string} [competenceId] - ID de la compétence (optionnel, pour filtrer)
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getEmployes(etablissementId, siteId, competenceId = null) {
  const query = supabase
    .from('employes')
    .select(
      `
      *,
      competences:employes_competences(
        competence:competence_id(id, code, libelle)
      )
    `
    )
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .eq('actif', true)
    .order('priorite', { ascending: true, nullsLast: true })
    .order('nom_complet', { ascending: true });
  const { data, error } = await query;

  // Filtrer par compétence si nécessaire
  if (!error && data && competenceId) {
    return {
      data: data.filter((employe) =>
        employe.competences?.some((ec) => ec.competence?.id === competenceId)
      ),
      error: null,
    };
  }

  return { data, error };
}

/**
 * Récupère les créneaux disponibles depuis la vue
 * @param {object} params
 * @param {string} params.etablissementId - ID de l'établissement
 * @param {string} params.siteId - ID du site
 * @param {Date} params.dateDebut - Date de début
 * @param {Date} params.dateFin - Date de fin
 * @param {string} [params.employeId] - ID de l'employé (optionnel)
 * @param {string[]} [params.employesQualifiesIds] - IDs des employés qualifiés (mode sans préférence)
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getCreneauxDisponibles({
  etablissementId,
  siteId,
  dateDebut,
  dateFin,
  employeId,
  employesQualifiesIds,
}) {
  const qualifiedIds = normalizeIdList(employesQualifiesIds);

  const filteredRpc = await supabase
    .rpc('creneaux_disponibles_filtre', {
      p_etablissement_id: etablissementId,
      p_site_id: siteId,
      p_date_debut: dateDebut.toISOString(),
      p_date_fin: dateFin.toISOString(),
      p_employe_id: employeId || null,
      p_employes_qualifies_ids: employeId || qualifiedIds.length === 0 ? null : qualifiedIds,
    })
    .select('*')
    .order('debut')
    .limit(1000);

  if (!filteredRpc.error) {
    return {
      data: filteredRpc.data || [],
      error: null,
    };
  }

  return {
    data: null,
    error: filteredRpc.error,
  };
}

function getFilteredAvailabilityRpcName(source) {
  return source === 'admin'
    ? 'creneaux_disponibles_bo_filtre'
    : 'creneaux_disponibles_filtre';
}

async function fetchFilteredAvailability({
  source,
  etablissementId,
  siteId,
  dateDebut,
  dateFin,
  employeId = null,
  employesQualifiesIds = null,
}) {
  return supabase.rpc(getFilteredAvailabilityRpcName(source), {
    p_etablissement_id: etablissementId,
    p_site_id: siteId,
    p_date_debut: dateDebut.toISOString(),
    p_date_fin: dateFin.toISOString(),
    p_employe_id: employeId,
    p_employes_qualifies_ids: employesQualifiesIds,
  });
}

export async function getPlanningPublicEndDate({ etablissementId, siteId, dateDebut }) {
  let query = supabase
    .from('planning_jours')
    .select('date_jour')
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .order('date_jour', { ascending: false })
    .limit(1);

  if (dateDebut instanceof Date && !Number.isNaN(dateDebut.getTime())) {
    const year = dateDebut.getFullYear();
    const month = String(dateDebut.getMonth() + 1).padStart(2, '0');
    const day = String(dateDebut.getDate()).padStart(2, '0');
    query = query.gte('date_jour', `${year}-${month}-${day}`);
  }

  const { data, error } = await query.maybeSingle();
  return {
    data: data?.date_jour || null,
    error,
  };
}

export async function verifierCreneauxToujoursDisponibles({
  etablissementId,
  siteId,
  employeId,
  creneauId,
  start,
  requiredMinutes,
  source,
}) {
  if (!etablissementId || !siteId || !employeId || !creneauId || !start) {
    return { data: false, error: new Error('Paramètres manquants pour vérifier la disponibilité.') };
  }

  const required = parseInt(requiredMinutes, 10);
  if (!Number.isFinite(required) || required <= 0) {
    return { data: false, error: new Error('Durée requise invalide.') };
  }

  const requestedStart = start instanceof Date ? start : new Date(start);
  if (Number.isNaN(requestedStart.getTime())) {
    return { data: false, error: new Error('Date de début invalide.') };
  }

  const windowEnd = new Date(requestedStart.getTime() + required * 60000);
  const filteredResult = await fetchFilteredAvailability({
    source,
    etablissementId,
    siteId,
    dateDebut: requestedStart,
    dateFin: windowEnd,
    employeId,
  });

  if (filteredResult.error) {
    return { data: false, error: filteredResult.error };
  }

  const slots = (filteredResult.data || [])
    .filter((slot) => new Date(slot.debut).getTime() < windowEnd.getTime())
    .sort((a, b) => new Date(a.debut) - new Date(b.debut));

  return verifierSuiteCreneaux({
    slots,
    creneauId,
    required,
  });
}

function verifierSuiteCreneaux({ slots, creneauId, required }) {
  if (!Array.isArray(slots) || slots.length === 0) {
    return { data: false, error: null };
  }

  const first = slots[0];
  const firstStart = new Date(first.debut);
  const endFirst = new Date(first.fin);
  if (Number.isNaN(firstStart.getTime()) || Number.isNaN(endFirst.getTime())) {
    return { data: false, error: new Error('Créneau invalide.') };
  }

  let stepMinutes = parseInt(first.duree_minutes, 10);
  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) {
    stepMinutes = Math.round((endFirst.getTime() - firstStart.getTime()) / 60000);
  }

  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) {
    return { data: false, error: new Error('Durée de créneau invalide.') };
  }

  const neededSlots = Math.ceil(required / stepMinutes);
  if (slots.length < neededSlots || String(first.id) !== String(creneauId)) {
    return { data: false, error: null };
  }

  for (let i = 1; i < neededSlots; i++) {
    const prevEnd = new Date(slots[i - 1].fin);
    const curStart = new Date(slots[i].debut);
    if (
      Number.isNaN(prevEnd.getTime()) ||
      Number.isNaN(curStart.getTime()) ||
      curStart.getTime() !== prevEnd.getTime()
    ) {
      return { data: false, error: null };
    }
  }

  return { data: true, error: null };
}

export async function verifierDisponibiliteSansPreference({
  etablissementId,
  siteId,
  start,
  requiredMinutes,
  employesQualifiesIds,
}) {
  if (!etablissementId || !siteId || !start) {
    return { data: false, error: new Error('Paramètres manquants pour vérifier la disponibilité.') };
  }

  const required = parseInt(requiredMinutes, 10);
  if (!Number.isFinite(required) || required <= 0) {
    return { data: false, error: new Error('Durée requise invalide.') };
  }

  const startDate = start instanceof Date ? start : new Date(start);
  if (Number.isNaN(startDate.getTime())) {
    return { data: false, error: new Error('Date de début invalide.') };
  }

  const windowEnd = new Date(startDate.getTime() + required * 60000);
  const hasQualifiedFilter = Array.isArray(employesQualifiesIds);
  const qualifiedIds = normalizeIdList(employesQualifiesIds);

  if (hasQualifiedFilter && qualifiedIds.length === 0) {
    return { data: false, error: null };
  }

  const filteredResult = await supabase.rpc('creneaux_disponibles_filtre', {
    p_etablissement_id: etablissementId,
    p_site_id: siteId,
    p_date_debut: startDate.toISOString(),
    p_date_fin: windowEnd.toISOString(),
    p_employe_id: null,
    p_employes_qualifies_ids: qualifiedIds.length > 0 ? qualifiedIds : null,
  });

  if (filteredResult.error) {
    return { data: false, error: filteredResult.error };
  }

  const slots = (filteredResult.data || []).filter(
    (slot) => new Date(slot.debut).getTime() < windowEnd.getTime()
  );

  if (!Array.isArray(slots) || slots.length === 0) {
    return { data: false, error: null };
  }

  const byEmploye = new Map();
  for (const s of slots) {
    const empId = String(s.employe_id || '');
    if (!empId) continue;
    if (!byEmploye.has(empId)) byEmploye.set(empId, []);
    byEmploye.get(empId).push(s);
  }

  for (const empSlots of byEmploye.values()) {
    const startSlot = empSlots.find((s) => {
      const d = new Date(s.debut);
      return !Number.isNaN(d.getTime()) && d.getTime() === startDate.getTime();
    });
    if (!startSlot) continue;

    const debut0 = new Date(startSlot.debut);
    const fin0 = new Date(startSlot.fin);
    if (Number.isNaN(debut0.getTime()) || Number.isNaN(fin0.getTime())) continue;

    let stepMinutes = parseInt(startSlot.duree_minutes, 10);
    if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) {
      stepMinutes = Math.round((fin0.getTime() - debut0.getTime()) / 60000);
    }
    if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) continue;

    const neededSlots = Math.ceil(required / stepMinutes);
    const expectedTimes = new Set();
    for (const s of empSlots) {
      const d = new Date(s.debut);
      if (!Number.isNaN(d.getTime())) expectedTimes.add(d.getTime());
    }

    let ok = true;
    for (let i = 0; i < neededSlots; i++) {
      const t = startDate.getTime() + i * stepMinutes * 60000;
      if (!expectedTimes.has(t)) {
        ok = false;
        break;
      }
    }

    if (ok) {
      return { data: true, error: null };
    }
  }

  return { data: false, error: null };
}

export async function verifierDisponibiliteEmployeAuDebut({
  etablissementId,
  siteId,
  employeId,
  start,
  requiredMinutes,
  source,
}) {
  if (!etablissementId || !siteId || !employeId || !start) {
    return { data: false, error: new Error('Paramètres manquants pour vérifier la disponibilité.') };
  }

  const required = parseInt(requiredMinutes, 10);
  if (!Number.isFinite(required) || required <= 0) {
    return { data: false, error: new Error('Durée requise invalide.') };
  }

  const startDate = start instanceof Date ? start : new Date(start);
  if (Number.isNaN(startDate.getTime())) {
    return { data: false, error: new Error('Date de début invalide.') };
  }

  const requestedWindowEnd = new Date(startDate.getTime() + required * 60000);
  const filteredResult = await fetchFilteredAvailability({
    source,
    etablissementId,
    siteId,
    dateDebut: startDate,
    dateFin: requestedWindowEnd,
    employeId,
  });

  if (filteredResult.error) {
    return { data: false, error: filteredResult.error };
  }

  const slots = (filteredResult.data || [])
    .filter((slot) => new Date(slot.debut).getTime() < requestedWindowEnd.getTime())
    .sort((a, b) => new Date(a.debut) - new Date(b.debut));
  const first = slots.find(
    (slot) => new Date(slot.debut).getTime() === startDate.getTime()
  );

  if (!first) {
    // Le premier créneau n'existe pas dans la vue des disponibles
    // Vérifier s'il existe dans la table creneaux (pour distinguer manquant vs occupé)
    const { data: creneauBrut } = await supabase
      .from('creneaux')
      .select('id, statut')
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .eq('employe_id', employeId)
      .eq('debut', startDate.toISOString())
      .maybeSingle();

    if (!creneauBrut) {
      return { 
        data: false, 
        error: null,
        reason: 'missing',  // Créneau de début non généré
        details: {
          startTime: startDate.toISOString()
        }
      };
    } else {
      return { 
        data: false, 
        error: null,
        reason: 'occupied',  // Créneau de début occupé
        details: {
          startTime: startDate.toISOString(),
          status: creneauBrut.statut
        }
      };
    }
  }

  const endFirst = new Date(first.fin);
  let stepMinutes = parseInt(first.duree_minutes, 10);
  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) {
    stepMinutes = Math.round((endFirst.getTime() - startDate.getTime()) / 60000);
  }
  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) {
    return { data: false, error: new Error('Durée de créneau invalide.') };
  }

  const neededSlots = Math.ceil(required / stepMinutes);
  const endDate = new Date(startDate.getTime() + neededSlots * stepMinutes * 60000);

  if (!Array.isArray(slots) || slots.length < neededSlots) {
    // Vérifier combien de créneaux existent réellement (pas juste dans la vue)
    const { data: allCreneaux } = await supabase
      .from('creneaux')
      .select('id, debut, statut')
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .eq('employe_id', employeId)
      .gte('debut', startDate.toISOString())
      .lt('debut', endDate.toISOString())
      .order('debut')
      .limit(neededSlots);

    const totalFound = Array.isArray(allCreneaux) ? allCreneaux.length : 0;
    const availableFound = Array.isArray(slots) ? slots.length : 0;

    if (totalFound < neededSlots) {
      return { 
        data: false, 
        error: null,
        reason: 'missing',
        details: {
          needed: neededSlots,
          found: totalFound,
          missing: neededSlots - totalFound
        }
      };
    } else {
      return { 
        data: false, 
        error: null,
        reason: 'occupied',
        details: {
          needed: neededSlots,
          available: availableFound,
          occupied: totalFound - availableFound
        }
      };
    }
  }

  for (let i = 1; i < neededSlots; i++) {
    const prevEnd = new Date(slots[i - 1].fin);
    const curStart = new Date(slots[i].debut);
    if (Number.isNaN(prevEnd.getTime()) || Number.isNaN(curStart.getTime())) {
      return { 
        data: false, 
        error: null,
        reason: 'gap',
        details: {
          slotIndex: i,
          prevEnd: prevEnd.toISOString(),
          curStart: curStart.toISOString()
        }
      };
    }
    if (curStart.getTime() !== prevEnd.getTime()) {
      return { 
        data: false, 
        error: null,
        reason: 'gap',
        details: {
          slotIndex: i,
          prevEnd: prevEnd.toISOString(),
          curStart: curStart.toISOString()
        }
      };
    }
  }

  return { data: true, error: null, reason: 'ok' };
}

export async function verifierCreneauxLibresSurPlage({
  etablissementId,
  siteId,
  employeId,
  start,
  end,
  stepMinutes,
  source
}) {
  if (!etablissementId || !siteId || !employeId || !start || !end) {
    return { data: false, error: new Error('Paramètres manquants pour vérifier la disponibilité.') };
  }

  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return { data: false, error: new Error('Dates invalides.') };
  }

  if (endDate.getTime() <= startDate.getTime()) {
    return { data: true, error: null };
  }

  const step = parseInt(stepMinutes, 10);
  if (!Number.isFinite(step) || step <= 0) {
    return { data: false, error: new Error('Durée de créneau invalide.') };
  }

  const neededSlots = Math.ceil((endDate.getTime() - startDate.getTime()) / (step * 60000));

  // Si source 'admin', on utilise la vue BO qui permet de voir les créneaux passés (si configuré côté SQL)
  // et qui ne filtre que les DISPONIBLES.
  if (source === 'admin') {
    const { data: boData, error: boError } = await fetchFilteredAvailability({
      source,
      etablissementId,
      siteId,
      dateDebut: startDate,
      dateFin: endDate,
      employeId,
    });

    if (boError) {
      return { data: false, error: boError };
    }

    const boSlots = (boData || [])
      .filter((slot) => new Date(slot.debut).getTime() < endDate.getTime())
      .sort((a, b) => new Date(a.debut) - new Date(b.debut))
      .slice(0, neededSlots);

    const availableCount = Array.isArray(boSlots) ? boSlots.length : 0;
    
    // Si on a tous les créneaux nécessaires, c'est bon
    if (availableCount >= neededSlots) {
       // Contrôle de continuité basique
      for (let i = 0; i < neededSlots; i++) {
        const expected = startDate.getTime() + i * step * 60000;
        const curStart = new Date(boSlots[i].debut);
        if (Number.isNaN(curStart.getTime()) || curStart.getTime() !== expected) {
          return { 
            data: false, 
            error: null, 
            reason: 'gap', 
            details: { expectedTime: new Date(expected).toISOString(), got: boSlots[i]?.debut } 
          };
        }
      }
      return { data: true, error: null, reason: 'ok' };
    } else {
       // Sinon, incomplet (manquant ou occupé)
       return { 
        data: false, 
        error: null, 
        reason: 'unavailable',
        details: { needed: neededSlots, available: availableCount } 
      };
    }
  }

  // 1. Logique standard (source != admin) : on vérifie la table brute 'creneaux'
  // D'abord, vérifier combien de créneaux EXISTENT (libre ou non) dans la plage
  const { data: allSlots, error: allError } = await supabase
    .from('creneaux')
    .select('id, debut, fin, statut')
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .eq('employe_id', employeId)
    .gte('debut', startDate.toISOString())
    .lt('debut', endDate.toISOString())
    .order('debut')
    .limit(neededSlots);

  if (allError) {
    return { data: false, error: allError };
  }

  // 2. Déterminer combien sont LIBRES.
  // IMPORTANT : L'UI considère un statut NULL/undefined comme "libre".
  // Pour éviter les faux positifs côté pré-check (ex: déplacement 18:05 -> 18:10),
  // on aligne la logique ici.
  const freeSlots = (Array.isArray(allSlots) ? allSlots : []).filter((slot) => {
    const statut = slot?.statut;
    return !statut || statut === 'libre';
  });

  // 3. Diagnostic : créneaux manquants vs créneaux occupés
  const totalSlotsFound = Array.isArray(allSlots) ? allSlots.length : 0;
  const freeSlotsFound = Array.isArray(freeSlots) ? freeSlots.length : 0;

  // Si moins de créneaux existent que nécessaire, c'est un problème de génération
  if (totalSlotsFound < neededSlots) {
    return { 
      data: false, 
      error: null,
      reason: 'missing',  // Créneaux manquants (pas générés)
      details: {
        needed: neededSlots,
        found: totalSlotsFound,
        missing: neededSlots - totalSlotsFound
      }
    };
  }

  // Si tous les créneaux existent mais certains ne sont pas libres, c'est un conflit
  if (freeSlotsFound < neededSlots) {
    return { 
      data: false, 
      error: null,
      reason: 'occupied',  // Créneaux occupés par une réservation/indisponibilité
      details: {
        needed: neededSlots,
        free: freeSlotsFound,
        occupied: totalSlotsFound - freeSlotsFound
      }
    };
  }

  // 4. Vérification de la continuité (comme avant)
  for (let i = 0; i < neededSlots; i++) {
    const expected = startDate.getTime() + i * step * 60000;
    const curStart = new Date(freeSlots[i].debut);
    if (Number.isNaN(curStart.getTime()) || curStart.getTime() !== expected) {
      return { 
        data: false, 
        error: null,
        reason: 'gap',  // Trou dans la continuité
        details: {
          expectedTime: new Date(expected).toISOString(),
          got: freeSlots[i]?.debut,
        }
      };
    }
  }

  return { data: true, error: null, reason: 'ok' };
}

export async function verifierIndisponibilitePossible({
  etablissementId,
  siteId,
  employeId,
  debut,
  fin,
  excludeIndispoId,
}) {
  if (!etablissementId || !employeId || !debut || !fin) {
    return { data: false, error: new Error('Paramètres manquants pour vérifier l’indisponibilité.') };
  }

  const startDate = debut instanceof Date ? debut : new Date(debut);
  const endDate = fin instanceof Date ? fin : new Date(fin);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return { data: false, error: new Error('Dates invalides.') };
  }

  if (endDate.getTime() <= startDate.getTime()) {
    return { data: false, error: new Error('La date de fin doit être après la date de début.') };
  }

  let indispoQuery = supabase
    .from('absences_employes')
    .select('id')
    .eq('etablissement_id', etablissementId)
    .eq('employe_id', employeId)
    .lt('debut', endDate.toISOString())
    .gt('fin', startDate.toISOString());

  if (excludeIndispoId) {
    indispoQuery = indispoQuery.neq('id', excludeIndispoId);
  }
  
  indispoQuery = indispoQuery.limit(1);

  const { data: indData, error: indError } = await indispoQuery;
  if (indError) {
    return { data: false, error: indError };
  }
  if (Array.isArray(indData) && indData.length > 0) {
    return { data: false, error: null };
  }

  let resQuery = supabase
    .from('reservations')
    .select('id')
    .eq('etablissement_id', etablissementId)
    .eq('employe_id', employeId)
    .neq('statut', 'annulee')
    .lt('debut', endDate.toISOString())
    .gt('fin', startDate.toISOString());

  if (siteId) {
    resQuery = resQuery.eq('site_id', siteId);
  }

  resQuery = resQuery.limit(1);

  const { data: resData, error: resError } = await resQuery;
  if (resError) {
    return { data: false, error: resError };
  }
  if (Array.isArray(resData) && resData.length > 0) {
    return { data: false, error: null };
  }

  return { data: true, error: null };
}

/**
 * Réserve un créneau via l'edge function WebEdena
 * @param {object} payload
 * @param {string} payload.creneau_id - ID du créneau
 * @param {string} payload.client_id - ID du client
 * @param {string} [payload.prestation_id] - ID de la prestation principale (optionnel en cas de multi-prestations)
 * @param {number} [payload.duree_manuelle_min] - Durée manuelle agrégée en minutes
 * @param {number} [payload.nombre_creneaux] - Nombre de créneaux utilisés
 * @param {string[]} [payload.prestations_ids] - Liste complète des prestations (badges)
 * @param {string} [payload.notes] - Notes optionnelles
 * @param {string} [payload.offre_id] - ID de l'offre
 * @param {string} [payload.beneficiaire_prenom] - Prénom de la personne qui recevra la prestation
 * @param {string} [payload.beneficiaire_nom] - Nom de la personne qui recevra la prestation
 * @returns {Promise<{data: object, error: object}>}
 */
export async function reserverCreneau({
  creneau_id,
  client_id,
  prestation_id,
  duree_manuelle_min,
  nombre_creneaux,
  prestations_ids,
  notes,
  source,
  sans_preference_employe,
  employes_qualifies_ids,
  offre_id,
  beneficiaire_prenom,
  beneficiaire_nom,
}) {
  // S'assurer que le JWT est bien envoyé
  let headers = undefined;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (token) headers = { Authorization: `Bearer ${token}` };
  } catch {}

  const { data, error } = await invokeEdgeFunction('reserver-creneau', {
    body: {
      creneau_id,
      client_id,
      prestation_id,
      duree_manuelle_min,
      nombre_creneaux,
      prestations_ids,
      notes,
      source,
      sans_preference_employe,
      employes_qualifies_ids,
      offre_id,
      beneficiaire_prenom,
      beneficiaire_nom,
    },
    headers,
  }, { component: 'booking', action: 'reserverCreneau' });

  if (error) {
    return { data: null, error: new Error(error.message || 'Erreur de réservation') };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || 'Erreur de réservation') };
}

/**
 * Récupère les réservations d'un client depuis la vue
 * @param {string} clientId - ID du client
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getMesReservations(clientId) {
  const { data, error } = await supabase
    .from('v_reservations_details')
    .select('*')
    .eq('client_id', clientId)
    .order('debut', { ascending: false });

  // Note: la vue v_reservations_details a déjà été mise à jour par la migration,
  // donc cette fonction devrait fonctionner correctement.

  return { data, error };
}

export async function getReservationsByClient(clientId) {
  if (!clientId) return { data: [], error: null };

  const { data, error } = await supabase
    .from('reservations')
    .select(`
      id,
      etablissement_id,
      site_id,
      client_id,
      debut,
      fin,
      statut,
      prix_applique_centimes,
      notes,
      beneficiaire_prenom,
      beneficiaire_nom,
      source,
      prestations_ids,
      cree_le,
      annulee_le,
      honore_le,
      non_honore_le,
      prestation:prestation_id (
        id,
        nom,
        duree_min
      ),
      employe:employe_id (
        id,
        nom_complet,
        couleur
      ),
      site:site_id (
        id,
        nom
      ),
      ressource:ressource_id (
        id,
        nom
      )
    `)
    .eq('client_id', clientId)
    .order('debut', { ascending: false });

  return { data: data || [], error };
}

/**
 * Annule une réservation via l'edge function
 * @param {string} reservationId - ID de la réservation
 * @returns {Promise<{data: object, error: object}>}
 */
export async function annulerReservation(reservationId) {
  // S'assurer que le JWT est bien envoyé à l'Edge Function
  let headers = undefined;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (token) headers = { Authorization: `Bearer ${token}` };
  } catch {}

  const { data, error } = await invokeEdgeFunction('annuler-reservation', {
    body: {
      reservation_id: reservationId,
      send_email: true,
      source: 'public',
    },
    headers,
  }, { component: 'reservation', action: 'annulerReservation' });

  if (error) {
    return { data: null, error: new Error(error.message || "Erreur d'annulation") };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || "Erreur d'annulation") };
}

/**
 * Annule une réservation via l'edge function (avec option d'envoi d'email)
 * @param {string} reservationId - ID de la réservation
 * @param {{ sendEmail?: boolean }} [options]
 * @returns {Promise<{data: object, error: object}>}
 */
export async function annulerReservationAvecOptions(reservationId, options = {}) {
  const sendEmail = options?.sendEmail !== undefined ? !!options.sendEmail : true;

  // S'assurer que le JWT est bien envoyé à l'Edge Function
  let headers = undefined;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (token) headers = { Authorization: `Bearer ${token}` };
  } catch {}

  const { data, error } = await invokeEdgeFunction('annuler-reservation', {
    body: {
      reservation_id: reservationId,
      send_email: sendEmail,
      source: 'admin',
    },
    headers,
  }, { component: 'reservation', action: 'annulerReservation' });

  if (error) {
    return { data: null, error: new Error(error.message || "Erreur d'annulation") };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || "Erreur d'annulation") };
}

/**
 * Envoie manuellement un email de confirmation pour une réservation (BO)
 * @param {string} reservationId - ID de la réservation
 * @returns {Promise<{data: object, error: object}>}
 */
export async function envoyerEmailConfirmationReservation(reservationId) {
  let headers = undefined;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (token) headers = { Authorization: `Bearer ${token}` };
  } catch {}

  const { data, error } = await invokeEdgeFunction('envoyer-confirmation-reservation', {
    body: { reservation_id: reservationId },
    headers,
  }, { component: 'reservation', action: 'envoyerEmailConfirmationReservation' });

  if (error) {
    return { data: null, error: new Error(error.message || "Erreur d'envoi de confirmation") };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || "Erreur d'envoi de confirmation") };
}

/**
 * Met à jour une réservation
 * @param {string} reservationId - ID de la réservation
 * @param {object} updates - Données à mettre à jour (client_id, prestation_id, notes)
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateReservation(reservationId, updates) {
  const { data, error } = await supabase
    .from('reservations')
    .update(updates)
    .eq('id', reservationId)
    .select(`
      *,
      client:client_id (
        id,
        prenom,
        nom,
        email,
        telephone
      ),
      prestation:prestation_id (
        id,
        nom,
        duree_min
      ),
      employe:employe_id (
        id,
        nom_complet,
        couleur
      )
    `)
    .single();

  return { data, error };
}

/**
 * Met à jour la durée d'une réservation via une Edge Function
 * @param {string} reservationId - ID de la réservation
 * @param {string} newFin - Nouvelle date de fin au format ISO
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateReservationDuration(reservationId, newFin, source = 'public') {
  const { data, error } = await invokeEdgeFunction('update-reservation-duration', {
    body: { 
      reservation_id: reservationId,
      new_fin: newFin,
      source
    },
  }, { component: 'planning', action: 'updateReservationDuration' });

  if (error) {
    return { data: null, error };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || "Erreur lors de la mise à jour de la durée") };
}

/**
 * Supprime une réservation
 * @param {string} reservationId - ID de la réservation
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteReservation(reservationId) {
  const { data, error } = await supabase
    .from('reservations')
    .delete()
    .eq('id', reservationId);

  return { data, error };
}

/**
 * Récupère les ressources actives
 * @param {string} [type] - Type de ressource ('cabine', 'equipement')
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getResources(type) {
  let query = supabase.from('resources').select('*').eq('active', true).order('name');

  if (type) {
    query = query.eq('type', type);
  }

  const { data, error } = await query;

  return { data, error };
}

// ===== FONCTIONS BACK-OFFICE - UTILISATEURS =====

/**
 * Récupère tous les utilisateurs d'un établissement
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getUtilisateurs(etablissementId) {
  const { data, error } = await invokeEdgeFunction('lister-utilisateurs', {
    body: { etablissement_id: etablissementId },
  }, { component: 'admin-utilisateurs', action: 'getUtilisateurs' });
  if (error) return { data: null, error };
  if (data?.success) return { data: data.data || [], error: null };
  return { data: null, error: new Error(data?.error || 'Erreur listing utilisateurs') };
}

/**
 * Crée un nouvel utilisateur (invitation)
 * @param {object} utilisateurData - { email, role, etablissement_id, nom, prenom }
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createUtilisateur(utilisateurData) {
  const { data, error } = await invokeEdgeFunction('inviter-utilisateur', {
    body: {
      email: utilisateurData.email,
      role: utilisateurData.role,
      etablissement_id: utilisateurData.etablissement_id,
      actif: utilisateurData.actif !== false,
    },
  }, { component: 'admin-utilisateurs', action: 'createUtilisateur' });

  if (error) return { data: null, error };
  if (data?.success) return { data: data.data, error: null };
  return { data: null, error: new Error(data?.error || 'Erreur invitation utilisateur') };
}


/**
 * Met à jour un utilisateur
 * @param {string} id - ID de l'utilisateur
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateUtilisateur(id, updates) {
  const { data, error } = await invokeEdgeFunction('modifier-utilisateur', {
    body: { utilisateur_id: id, updates },
  }, { component: 'admin-utilisateurs', action: 'updateUtilisateur' });
  if (error) return { data: null, error };
  if (data?.success) return { data: data.data, error: null };
  return { data: null, error: new Error(data?.error || 'Erreur mise à jour utilisateur') };
}

/**
 * Supprime un utilisateur
 * @param {string} id - ID de l'utilisateur dans notre table public.utilisateurs
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteUtilisateur(id) {
  const { data, error } = await invokeEdgeFunction('supprimer-utilisateur', {
    body: { utilisateur_id: id },
  }, { component: 'admin-utilisateurs', action: 'deleteUtilisateur' });
  if (error) return { data: null, error };
  if (data?.success) return { data: data.data, error: null };
  return { data: null, error: new Error(data?.error || 'Erreur suppression utilisateur') };
}

export async function getBoPagesPermissions(etablissementId) {
  const { data, error } = await invokeEdgeFunction('bo-pages-permissions-get', {
    body: { etablissement_id: etablissementId },
  }, { component: 'admin', action: 'getBoPagesPermissions' });
  if (error) return { data: null, error };
  if (data?.success) return { data: data.data || [], error: null };
  return { data: null, error: new Error(data?.error || 'Erreur lecture droits pages BO') };
}

export async function setBoPagesPermissions(etablissementId, rows) {
  const { data, error } = await invokeEdgeFunction('bo-pages-permissions-set', {
    body: { etablissement_id: etablissementId, rows: rows || [] },
  }, { component: 'admin', action: 'setBoPagesPermissions' });
  if (error) return { data: null, error };
  if (data?.success) return { data: data.data || [], error: null };
  return { data: null, error: new Error(data?.error || 'Erreur sauvegarde droits pages BO') };
}

/**
 * Dissocie un utilisateur de l'établissement (supprime la liaison)
 * @param {string} utilisateurId
 * @param {string} etablissementId
 * @returns {Promise<{data: object, error: object}>}
 */
export async function unlinkUtilisateurEtablissement(utilisateurId, etablissementId) {
  const { data, error } = await supabase
    .from('utilisateurs_etablissements')
    .delete()
    .eq('utilisateur_id', utilisateurId)
    .eq('etablissement_id', etablissementId)
    .select();
  return { data, error };
}

/**
 * Récupère tous les établissements
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getEtablissements() {
  const { data, error } = await supabase
    .from('etablissements')
    .select('*')
    .order('nom');

  return { data, error };
}

/**
 * Met à jour un établissement
 * @param {string} id - ID de l'établissement
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateEtablissement(id, updates) {
  const { data, error } = await supabase
    .from('etablissements')
    .update(updates)
    .eq('id', id)
    .select()
    .maybeSingle();

  return { data, error };
}


/**
 * Récupère l'ensemble des clients d'un établissement (sans pagination) pour l'export
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: Array|null, error: object|null}>}
 */
export async function getAllClients(etablissementId) {
  if (!etablissementId) return { data: [], error: null };
  const allClients = [];
  const pageSize = 1000;
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('etablissement_id', etablissementId)
      .order('nom', { ascending: true })
      .range(from, to);

    if (error) {
      return { data: null, error };
    }

    if (!data || data.length === 0) {
      break;
    }

    allClients.push(...data);
    if (data.length < pageSize) {
      hasMore = false;
    } else {
      page += 1;
    }
  }

  return { data: allClients, error: null };
}

export async function getClients(etablissementId, { page = 1, pageSize = 50, filter = '' } = {}) {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('clients')
    .select('*', { count: 'exact' })
    .eq('etablissement_id', etablissementId)
    .order('nom')
    .range(from, to);

  if (filter) {
    const searchTerm = `%${filter}%`;
    query = query.or(`nom.ilike.${searchTerm},prenom.ilike.${searchTerm},telephone.ilike.${searchTerm}`);
  }

  const { data, error, count } = await query;

  if (error) {
    return { data: null, error, count: 0 };
  }

  if (!data || data.length === 0) {
    return { data: [], error: null, count: 0 };
  }

  // Enrichir avec les données utilisateur
  const userIds = Array.from(new Set(data.flatMap(c => [c.utilisateur_id, c.cree_par]).filter(Boolean)));
  if (userIds.length > 0) {
    const { data: users, error: usersError } = await supabase
      .from('utilisateurs')
      .select('id, email, role, actif')
      .in('id', userIds);

    if (!usersError && users) {
      const usersById = new Map(users.map(u => [u.id, u]));
      const enrichedData = data.map(client => ({
        ...client,
        utilisateur: usersById.get(client.utilisateur_id) || null,
        createur: usersById.get(client.cree_par) || null,
      }));
      return { data: enrichedData, error: null, count };
    }
  }

  return { data, error: null, count };
}

export async function getUtilisateurByEmail(email) {
  const norm = (email || '').trim();
  if (!norm) return { data: null, error: null };
  const { data, error } = await supabase
    .from('utilisateurs')
    .select('id, email, role, actif')
    .ilike('email', norm)
    .maybeSingle();
  return { data, error };
}

export async function createPublicClient(clientData) {
  const { data, error } = await invokeEdgeFunction('creer-client-public', {
    body: clientData,
  }, { component: 'booking-public', action: 'createPublicClient' });

  if (error) {
    return { data: null, error: new Error(error.message || 'Erreur lors de la création du client.') };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || 'Erreur lors de la création du client.') };
}

export async function createClient(clientData, _options = {}) {
  let cree_par = null;
  let authId = null;

  try {
    const { data: authInfo } = await supabase.auth.getUser();
    authId = authInfo?.user?.id || null;

    if (authId) {
      const { data: selfRow } = await supabase
        .from('utilisateurs')
        .select('id')
        .eq('auth_user_id', authId)
        .maybeSingle();
      cree_par = selfRow?.id || null;
    }
  } catch {}

  const payload = { ...clientData, cree_par, utilisateur_id: null };
  const { data, error } = await supabase
    .from('clients')
    .insert([payload])
    .select()
    .single();

  if (error) {
    if (error.code === '23505' && (error.message || '').includes('clients_etablissement_id_email_key')) {
      return { data: null, error: new Error("Un client avec cet email existe déjà dans cet établissement.") };
    }
    return { data: null, error: new Error(error.message || 'Erreur lors de la création du client.') };
  }

  return { data, error: null };
}

/**
 * Met à jour un client
 * @param {string} id - ID du client
 * @param {object} updates - Données à mettre à jour
 * @param {object} options - Options
 * @param {boolean} options.updateRoleToClient - Mettre à jour le rôle de l'utilisateur en 'client' si nécessaire
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateClient(id, updates, _options = {}) {
  const computed = {};

  if (Object.prototype.hasOwnProperty.call(updates, 'email')) {
    if (updates.email) {
      computed.utilisateur_id = null;
    } else {
      computed.utilisateur_id = null;
    }
  }

  const { data, error } = await supabase
    .from('clients')
    .update({ ...updates, ...computed })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    if (error.code === '23505' && (error.message || '').includes('clients_etablissement_id_email_key')) {
      return { data: null, error: new Error("Un client avec cet email existe déjà dans cet établissement.") };
    }
    return { data: null, error: new Error(error.message || 'Erreur lors de la mise à jour du client.') };
  }

  return { data, error: null };
}

export async function deleteClient(id) {
  const { data, error } = await supabase
    .from('clients')
    .delete()
    .eq('id', id)
    .select()
    .maybeSingle();

  return { data, error };
}

/**
 * Compte les réservations d'un client
 * @param {string} clientId - ID du client
 * @returns {Promise<{count: number, error: object}>}
 */
export async function getReservationsCountByClient(clientId) {
  const { count, error } = await supabase
    .from('reservations')
    .select('*', { count: 'exact', head: true })
    .eq('client_id', clientId);

  return { count: count || 0, error };
}

/**
 * Compte les reservations payees par categorie sur une campagne
 * @param {object} params
 * @param {string} params.categorieId
 * @param {Date|string} params.startDate
 * @param {Date|string} params.endDate
 * @returns {Promise<{count: number, error: object|null}>}
 */
export async function getOfferPaidCountByCategorie({ categorieId, startDate, endDate }) {
  if (!categorieId || !startDate || !endDate) {
    return { count: 0, error: null };
  }

  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { count: 0, error: new Error('Dates invalides pour le compteur d\'offre.') };
  }

  const { count, error } = await supabase
    .from('reservations')
    .select('id, prestation:prestation_id!inner(categorie_id)', { count: 'exact', head: true })
    .eq('prestation.categorie_id', categorieId)
    .eq('statut', 'confirmee')
    .gt('prix_applique_centimes', 0)
    .gte('debut', start.toISOString())
    .lte('debut', end.toISOString());

  return { count: count || 0, error };
}

// ===== FONCTIONS BACK-OFFICE =====

/**
 * Crée un nouvel employé
 * @param {object} employeData - Données de l'employé
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createEmploye(employeData) {
  const { data, error } = await supabase
    .from('employes')
    .insert([employeData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour un employé
 * @param {string} id - ID de l'employé
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateEmploye(id, updates) {
  const { data, error } = await supabase
    .from('employes')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime un employé (désactivation)
 * @param {string} id - ID de l'employé
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteEmploye(id) {
  const { data, error } = await supabase
    .from('employes')
    .update({ actif: false })
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Récupère toutes les catégories
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getCategories(etablissementId) {
  const { data, error } = await supabase
    .from('categories_prestations')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .order('libelle');

  return { data, error };
}

/**
 * Crée une nouvelle catégorie
 * @param {object} categoryData - Données de la catégorie
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createCategorie(categoryData) {
  const { data, error } = await supabase
    .from('categories_prestations')
    .insert([categoryData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour une catégorie
 * @param {string} id - ID de la catégorie
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateCategorie(id, updates) {
  const { data, error } = await supabase
    .from('categories_prestations')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime une catégorie
 * @param {string} id - ID de la catégorie
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteCategorie(id) {
  const { data, error } = await supabase.from('categories_prestations').delete().eq('id', id);

  return { data, error };
}

/**
 * Compte le nombre de prestations liées à une catégorie
 * @param {string} categorieId
 * @param {string} etablissementId
 * @returns {Promise<{count: number|null, error: object}>}
 */
export async function getPrestationsCountByCategorie(categorieId, etablissementId) {
  const { count, error } = await supabase
    .from('prestations_categories')
    .select('prestation_id', { count: 'exact', head: true })
    .eq('categorie_id', categorieId)
    .eq('prestation_id.etablissement_id', etablissementId); // Attention: ceci nécessite une jointure ou un filtre adapté

  // Si le filtre ci-dessus ne fonctionne pas (dépend de PostgREST), on peut faire plus simple:
  // La plupart du temps, categorieId suffit si les catégories sont propres à un établissement.
  
  return { count, error };
}

/**
 * Récupère les catégories liées à une prestation
 * @param {string} prestationId
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getPrestationCategories(prestationId) {
  const { data, error } = await supabase
    .from('prestations_categories')
    .select('categorie:categorie_id (*)')
    .eq('prestation_id', prestationId);

  return { data, error };
}

/**
 * Ajoute une catégorie à une prestation
 * @param {string} prestationId
 * @param {string} categorieId
 * @returns {Promise<{data: object, error: object}>}
 */
export async function addPrestationCategorie(prestationId, categorieId) {
  const { data, error } = await supabase
    .from('prestations_categories')
    .insert([{ prestation_id: prestationId, categorie_id: categorieId }])
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime une catégorie d'une prestation
 * @param {string} prestationId
 * @param {string} categorieId
 * @returns {Promise<{data: object, error: object}>}
 */
export async function removePrestationCategorie(prestationId, categorieId) {
  const { data, error } = await supabase
    .from('prestations_categories')
    .delete()
    .eq('prestation_id', prestationId)
    .eq('categorie_id', categorieId);

  return { data, error };
}

/**
 * Crée une nouvelle prestation
 * @param {object} prestationData - Données de la prestation
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createPrestation(prestationData) {
  const { data, error } = await supabase
    .from('prestations')
    .insert([prestationData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour une prestation
 * @param {string} id - ID de la prestation
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updatePrestation(id, updates) {
  const { data, error } = await supabase
    .from('prestations')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime une prestation (désactivation)
 * @param {string} id - ID de la prestation
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deletePrestation(id) {
  const { data, error } = await supabase
    .from('prestations')
    .update({ active: false })
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}


/**
 * Récupère les listes de tarifs d'un établissement (triées par date d'effet desc)
 * @param {string} etablissementId
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getListesTarifs(etablissementId) {
  const { data, error } = await supabase
    .from('listes_tarifs')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .order('date_effet', { ascending: false });
  return { data, error };
}

function formatLocalDate(date) {
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) {
    return null;
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Récupère les tarifs applicables à une date pour le parcours public.
 * La sélection explicite de la liste évite qu'une session back-office voie
 * une liste future ou inactive que le visiteur anonyme ne peut pas voir.
 * @param {string} etablissementId
 * @param {Date|string} [referenceDate]
 * @returns {Promise<{data: Array, error: object|null, liste: object|null}>}
 */
export async function getTarifsActuels(etablissementId, referenceDate = new Date()) {
  const dateEffetMax = formatLocalDate(referenceDate);
  if (!dateEffetMax) {
    return {
      data: [],
      error: new Error('Date de référence invalide pour le chargement des tarifs'),
      liste: null,
    };
  }

  const { data: listes, error: listesError } = await supabase
    .from('listes_tarifs')
    .select('id, date_effet')
    .eq('etablissement_id', etablissementId)
    .eq('actif', true)
    .lte('date_effet', dateEffetMax)
    .order('date_effet', { ascending: false })
    .limit(1);

  if (listesError) {
    return { data: [], error: listesError, liste: null };
  }

  const liste = Array.isArray(listes) ? listes[0] || null : null;
  if (!liste?.id) {
    return { data: [], error: null, liste: null };
  }

  const { data, error } = await supabase
    .from('listes_tarifs_prestations')
    .select('prestation_id, prix_centimes_public, prix_centimes_abonne')
    .eq('liste_id', liste.id);

  return { data: data || [], error, liste };
}

/**
 * Crée une liste de tarifs
 * @param {object} listeData - { etablissement_id, nom?, date_effet, actif? }
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createListeTarifs(listeData) {
  const { data, error } = await supabase
    .from('listes_tarifs')
    .insert([listeData])
    .select()
    .single();
  return { data, error };
}

/**
 * Met à jour une liste de tarifs
 * @param {string} id
 * @param {object} updates
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateListeTarifs(id, updates) {
  const { data, error } = await supabase
    .from('listes_tarifs')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

/**
 * Supprime une liste de tarifs
 * @param {string} id
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteListeTarifs(id) {
  const { data, error } = await supabase
    .from('listes_tarifs')
    .delete()
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

/**
 * Récupère les tarifs d'une liste pour chaque prestation
 * @param {string} listeId
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getListeTarifsPrestations(listeId) {
  const { data, error } = await supabase
    .from('listes_tarifs_prestations')
    .select('*')
    .eq('liste_id', listeId);
  return { data, error };
}

/**
 * Upsert d'un tarif de prestation dans une liste
 * @param {string} listeId
 * @param {string} prestationId
 * @param {number|null} prixPublicCentimes
 * @param {number|null} prixAbonneCentimes
 * @returns {Promise<{data: object, error: object}>}
 */
export async function upsertListeTarifPrestation(
  listeId,
  prestationId,
  prixPublicCentimes,
  prixAbonneCentimes
) {
  const row = {
    liste_id: listeId,
    prestation_id: prestationId,
    prix_centimes_public:
      prixPublicCentimes === null || prixPublicCentimes === undefined
        ? null
        : parseInt(prixPublicCentimes, 10),
    prix_centimes_abonne:
      prixAbonneCentimes === null || prixAbonneCentimes === undefined
        ? null
        : parseInt(prixAbonneCentimes, 10),
  };

  const { data, error } = await supabase
    .from('listes_tarifs_prestations')
    .upsert([row], { onConflict: 'liste_id,prestation_id' })
    .select()
    .single();

  return { data, error };
}

/**
 * Récupère les créneaux pour une journée (vue planning)
 * @param {object} params
 * @param {string} params.etablissementId - ID de l'établissement
 * @param {string} params.siteId - ID du site
 * @param {Date} params.date - Date à afficher
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getCreneauxJournee({ etablissementId, siteId, date }) {
  try {
    // Calculer les bornes de la journée dans le fuseau horaire du site
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0); // Minuit local
    
    const startOfDay = new Date(localDate);
    const endOfDay = new Date(localDate);
    endOfDay.setHours(23, 59, 59, 999); // Fin de journée locale

    const { data, error } = await supabase
      .from('creneaux')
      .select(`
        *,
        employe:employe_id(id, nom_complet, couleur),
        reservation:reservation_id (
          id,
          cree_le,
          statut,
          debut,
          fin,
          notes,
          client_id,
          client:client_id (id, prenom, nom, email),
          prestation_id,
          prestations_ids,
          beneficiaire_prenom,
          beneficiaire_nom,
          honore_le,
          non_honore_le,
          annulee_le,
          annulee_source,
          source
        )
      `)
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .gte('debut', startOfDay.toISOString())
      .lte('debut', endOfDay.toISOString())
      .order('debut');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Erreur getCreneauxJournee:', error);
    return { data: null, error };
  }
}

/**
 * Récupère toutes les réservations d'une journée (indépendamment des créneaux)
 * Cette fonction garantit que toutes les réservations sont chargées, même si elles ne sont pas liées aux créneaux
 * @param {object} params
 * @param {string} params.etablissementId - ID de l'établissement
 * @param {string} params.siteId - ID du site
 * @param {Date|string} params.date - Date de la journée
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getReservationsJournee({ etablissementId, siteId, date }) {
  try {
    // Calculer les bornes de la journée
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    
    const startOfDay = new Date(localDate);
    const endOfDay = new Date(localDate);
    endOfDay.setHours(23, 59, 59, 999);

    const { data, error } = await supabase
      .from('reservations')
      .select(`
        id,
        cree_le,
        debut,
        fin,
        statut,
        notes,
        prix_applique_centimes,
        source,
        annulee_le,
        annulee_source,
        honore_le,
        non_honore_le,
        employe_id,
        client_id,
        prestation_id,
        prestations_ids,
        beneficiaire_prenom,
        beneficiaire_nom,
        client:client_id (id, prenom, nom, email, telephone),
        prestation:prestation_id (id, nom, duree_min),
        employe:employe_id (id, nom_complet, couleur)
      `)
      .eq('etablissement_id', etablissementId)
      .eq('site_id', siteId)
      .gte('debut', startOfDay.toISOString())
      .lte('debut', endOfDay.toISOString())
      .order('debut');

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Erreur getReservationsJournee:', error);
    return { data: [], error };
  }
}

/**
 * Récupère la configuration de planning pour une journée donnée
 * @param {string} etablissementId
 * @param {string} siteId
 * @param {string} dateJour - au format 'YYYY-MM-DD'
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function getPlanningJour(etablissementId, siteId, dateJour) {
  if (!etablissementId || !siteId || !dateJour) {
    return { data: null, error: null };
  }

  const { data, error } = await supabase
    .from('planning_jours')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .eq('date_jour', dateJour)
    .maybeSingle();

  return { data: data || null, error };
}

/**
 * Récupère toutes les journées de planning d'un site
 * @param {string} etablissementId
 * @param {string} siteId
 * @returns {Promise<{data: Array, error: object|null}>}
 */
export async function getPlanningJours(etablissementId, siteId) {
  if (!etablissementId || !siteId) {
    return { data: [], error: null };
  }

  const { data, error } = await supabase
    .from('planning_jours')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .order('date_jour', { ascending: false });

  return { data: data || [], error };
}

/**
 * Crée ou met à jour la configuration de planning pour une journée
 * @param {object} payload
 * @param {string} payload.etablissement_id
 * @param {string} payload.site_id
 * @param {string} payload.date_jour - 'YYYY-MM-DD'
 * @param {number} payload.duree_creneau_min
 * @param {boolean} payload.ouverte_reservation_publique
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function upsertPlanningJour(payload) {
  const normalizeTime = (val) => {
    if (val === undefined || val === null) return null;
    const s = String(val).trim();
    if (!s) return null;
    if (s.length === 5) return `${s}:00`;
    if (s.length >= 8) return s.slice(0, 8);
    return s;
  };

  const row = {
    etablissement_id: payload.etablissement_id,
    site_id: payload.site_id,
    date_jour: payload.date_jour,
    duree_creneau_min: parseInt(payload.duree_creneau_min, 10),
    ouverte_reservation_publique: !!payload.ouverte_reservation_publique,
    heure_ouverture: normalizeTime(payload.heure_ouverture),
    heure_fermeture: normalizeTime(payload.heure_fermeture),
  };

  const { data, error } = await supabase
    .from('planning_jours')
    .upsert([row], { onConflict: 'etablissement_id,site_id,date_jour' })
    .select('*')
    .single();

  return { data, error };
}

/**
 * Récupère les blocages de réservation publique pour une journée donnée (par employé)
 * @param {string} etablissementId
 * @param {string} siteId
 * @param {string} dateJour - au format 'YYYY-MM-DD'
 * @returns {Promise<{data: Array, error: object|null}>}
 */
export async function getPlanningBlocagesPublicsEmployesPourJour(etablissementId, siteId, dateJour) {
  if (!etablissementId || !siteId || !dateJour) {
    return { data: [], error: null };
  }

  const { data, error } = await supabase
    .from('planning_blocages_publics_employes')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .eq('date_jour', dateJour);

  return { data: data || [], error };
}

/**
 * Définit ou supprime le blocage de réservation publique pour un employé et une journée donnée
 * @param {string} etablissementId
 * @param {string} siteId
 * @param {string} employeId
 * @param {string} dateJour - 'YYYY-MM-DD'
 * @param {boolean} bloque - true pour bloquer en public, false pour autoriser
 * @param {string|null} [motif]
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function setPlanningBlocagePublicEmploye(
  etablissementId,
  siteId,
  employeId,
  dateJour,
  bloque,
  motif = null,
) {
  if (!etablissementId || !siteId || !employeId || !dateJour) {
    return { data: null, error: null };
  }

  if (bloque) {
    const row = {
      etablissement_id: etablissementId,
      site_id: siteId,
      employe_id: employeId,
      date_jour: dateJour,
      motif: motif || null,
    };

    const { data, error } = await supabase
      .from('planning_blocages_publics_employes')
      .upsert([row], { onConflict: 'etablissement_id,site_id,employe_id,date_jour' })
      .select('*')
      .single();

    return { data, error };
  }

  const { data, error } = await supabase
    .from('planning_blocages_publics_employes')
    .delete()
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .eq('employe_id', employeId)
    .eq('date_jour', dateJour)
    .select()
    .maybeSingle();

  return { data, error };
}

/**
 * Récupère toutes les ressources (ressources techniques)
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} siteId - ID du site
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getRessourcesAll(etablissementId, siteId) {
  const { data, error } = await supabase
    .from('ressources')
    .select(
      `
      *,
      competences:ressources_competences(
        competence:competence_id (id, code, libelle)
      )
    `,
    )
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .order('nom');

  return { data, error };
}

/**
 * Crée une nouvelle ressource
 * @param {object} ressourceData - Données de la ressource
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createRessource(ressourceData) {
  const { data, error } = await supabase
    .from('ressources')
    .insert([ressourceData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour une ressource
 * @param {string} id - ID de la ressource
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateRessource(id, updates) {
  const { data, error } = await supabase
    .from('ressources')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime une ressource (désactivation)
 * @param {string} id - ID de la ressource
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteRessource(id) {
  const { data, error } = await supabase
    .from('ressources')
    .update({ active: false })
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Récupère toutes les compétences d'un établissement avec leurs relations
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getCompetences(etablissementId) {
  const { data, error } = await supabase
    .from('competences')
    .select(
      `
      *,
      employes:employes_competences(count),
      prestations:prestations_competences(count),
      ressources:ressources_competences(count)
    `
    )
    .eq('etablissement_id', etablissementId)
    .order('libelle');

  return { data, error };
}

/**
 * Récupère toutes les indisponibilités des employés
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} [employeId] - ID de l'employé (optionnel, pour filtrer)
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getIndisponibilites(etablissementId, employeId = null) {
  let query = supabase
    .from('absences_employes')
    .select(
      `
      *,
      employe:employe_id (id, nom_complet)
    `
    )
    .eq('etablissement_id', etablissementId)
    .order('debut', { ascending: false });

  if (employeId) {
    query = query.eq('employe_id', employeId);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Crée une nouvelle indisponibilité
 * @param {object} indisponibiliteData - Données de l'indisponibilité
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createIndisponibilite(indisponibiliteData) {
  const { data, error } = await supabase
    .from('absences_employes')
    .insert([indisponibiliteData])
    .select(
      `
      *,
      employe:employe_id (id, nom_complet)
    `
    )
    .single();

  return { data, error };
}

/**
 * Met à jour une indisponibilité
 * @param {string} id - ID de l'indisponibilité
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateIndisponibilite(id, updates) {
  const { data, error } = await supabase
    .from('absences_employes')
    .update(updates)
    .eq('id', id)
    .select(
      `
      *,
      employe:employe_id (id, nom_complet)
    `
    )
    .single();

  return { data, error };
}

/**
 * Met à jour la durée d'une indisponibilité via une Edge Function
 * @param {string} indisponibiliteId - ID de l'indisponibilité
 * @param {string} newFin - Nouvelle date de fin au format ISO
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateIndisponibiliteDuration(indisponibiliteId, newFin) {
  const { data, error } = await invokeEdgeFunction('update-indisponibilite-duration', {
    body: { 
      indisponibilite_id: indisponibiliteId,
      new_fin: newFin
    },
  }, { component: 'planning', action: 'updateIndisponibiliteDuration' });

  if (error) {
    return { data: null, error };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || "Erreur lors de la mise à jour de la durée") };
}

/**
 * Supprime une indisponibilité
 * @param {string} id - ID de l'indisponibilité
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteIndisponibilite(id) {
  const { data, error } = await supabase
    .from('absences_employes')
    .delete()
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Déplace une indisponibilité d'employé via une fonction RPC
 * @param {string} indisponibiliteId - ID de l'indisponibilité
 * @param {string} newEmployeId - ID du nouvel employé
 * @param {string} newDebut - Nouvelle date de début (ISO string)
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deplacerIndisponibilite(indisponibiliteId, newEmployeId, newDebut) {
  // Récupérer l'ID de l'utilisateur authentifié pour le passer à la fonction RLS-aware
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { data: null, error: new Error('Utilisateur non authentifié.') };
  }

  const { data, error } = await supabase.rpc('deplacer_indisponibilite_atomique', {
    p_indisponibilite_id: indisponibiliteId,
    p_new_employe_id: newEmployeId,
    p_new_debut: newDebut,
    p_auth_user_id: user.id
  });

  if (error) {
    // Assurer que l'erreur a toujours un format `Error` avec un message
    const errorMessage = error.message || 'Erreur inconnue lors de l\'appel RPC.';
    return { data: null, error: new Error(errorMessage) };
  }

  // La fonction RPC elle-même peut retourner une erreur métier
  if (data && !data.success) {
      return { data: null, error: new Error(data.error || 'La fonction a échoué sans message spécifique.') };
  }

  return { data, error: null };
}

/**
 * Récupère toutes les indisponibilités des ressources
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} [ressourceId] - ID de la ressource (optionnel, pour filtrer)
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getIndisponibilitesRessources(etablissementId, ressourceId = null) {
  let query = supabase
    .from('indisponibilites_ressources')
    .select(
      `
      *,
      ressource:ressource_id (id, nom)
    `
    )
    .eq('etablissement_id', etablissementId)
    .order('debut', { ascending: false });

  if (ressourceId) {
    query = query.eq('ressource_id', ressourceId);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Crée une nouvelle indisponibilité de ressource
 * @param {object} indisponibiliteData - Données de l'indisponibilité
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createIndisponibiliteRessource(indisponibiliteData) {
  const { data, error } = await supabase
    .from('indisponibilites_ressources')
    .insert([indisponibiliteData])
    .select(
      `
      *,
      ressource:ressource_id (id, nom)
    `
    )
    .single();

  return { data, error };
}

/**
 * Met à jour une indisponibilité de ressource
 * @param {string} id - ID de l'indisponibilité
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateIndisponibiliteRessource(id, updates) {
  const { data, error } = await supabase
    .from('indisponibilites_ressources')
    .update(updates)
    .eq('id', id)
    .select(
      `
      *,
      ressource:ressource_id (id, nom)
    `
    )
    .single();

  return { data, error };
}

/**
 * Supprime une indisponibilité de ressource
 * @param {string} id - ID de l'indisponibilité
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteIndisponibiliteRessource(id) {
  const { data, error } = await supabase
    .from('indisponibilites_ressources')
    .delete()
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Crée une nouvelle compétence
 * @param {object} competenceData - Données de la compétence
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createCompetence(competenceData) {
  const { data, error } = await supabase
    .from('competences')
    .insert([competenceData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour une compétence
 * @param {string} id - ID de la compétence
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateCompetence(id, updates) {
  const { data, error } = await supabase
    .from('competences')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime une compétence
 * @param {string} id - ID de la compétence
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteCompetence(id) {
  const { data, error } = await supabase.from('competences').delete().eq('id', id).select().single();

  return { data, error };
}

/**
 * Récupère les compétences d'une prestation
 * @param {string} prestationId - ID de la prestation
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getPrestationCompetences(prestationId) {
  const { data, error } = await supabase
    .from('prestations_competences')
    .select(
      `
      competence:competence_id (id, code, libelle)
    `
    )
    .eq('prestation_id', prestationId);

  return { data, error };
}

/**
 * Ajoute une compétence à une prestation
 * @param {string} prestationId - ID de la prestation
 * @param {string} competenceId - ID de la compétence
 * @returns {Promise<{data: object, error: object}>}
 */
export async function addPrestationCompetence(prestationId, competenceId) {
  const { data, error } = await supabase
    .from('prestations_competences')
    .insert([{ prestation_id: prestationId, competence_id: competenceId }])
    .select();

  return { data, error };
}

/**
 * Retire une compétence d'une prestation
 * @param {string} prestationId - ID de la prestation
 * @param {string} competenceId - ID de la compétence
 * @returns {Promise<{data: object, error: object}>}
 */
export async function removePrestationCompetence(prestationId, competenceId) {
  const { data, error } = await supabase
    .from('prestations_competences')
    .delete()
    .eq('prestation_id', prestationId)
    .eq('competence_id', competenceId);

  return { data, error };
}

/**
 * Récupère les compétences d'une ressource
 * @param {string} ressourceId - ID de la ressource
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getRessourceCompetences(ressourceId) {
  const { data, error } = await supabase
    .from('ressources_competences')
    .select(
      `
      competence:competence_id (id, code, libelle)
    `
    )
    .eq('ressource_id', ressourceId);

  return { data, error };
}

/**
 * Ajoute une compétence à une ressource
 * @param {string} ressourceId - ID de la ressource
 * @param {string} competenceId - ID de la compétence
 * @returns {Promise<{data: object, error: object}>}
 */
export async function addRessourceCompetence(ressourceId, competenceId) {
  const { data, error } = await supabase
    .from('ressources_competences')
    .insert([{ ressource_id: ressourceId, competence_id: competenceId }])
    .select();

  return { data, error };
}

/**
 * Retire une compétence d'une ressource
 * @param {string} ressourceId - ID de la ressource
 * @param {string} competenceId - ID de la compétence
 * @returns {Promise<{data: object, error: object}>}
 */
export async function removeRessourceCompetence(ressourceId, competenceId) {
  const { data, error } = await supabase
    .from('ressources_competences')
    .delete()
    .eq('ressource_id', ressourceId)
    .eq('competence_id', competenceId);

  return { data, error };
}

/**
 * Récupère les horaires d'ouverture pour un site
 * @param {string} etablissementId
 * @param {string} siteId
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getHorairesOuverture(etablissementId, siteId) {
  const { data, error } = await supabase
    .from('horaires_ouverture')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .eq('site_id', siteId)
    .order('ordre_jour')
    .order('heure_ouverture');
  return { data, error };
}

/**
 * Crée un intervalle d'ouverture
 * @param {{ etablissement_id:string, site_id:string, jour_semaine:number, heure_ouverture:string, heure_fermeture:string }} row
 */
export async function createHoraireOuverture(rows) {
  const { data, error } = await supabase
    .from('horaires_ouverture')
    .insert(Array.isArray(rows) ? rows : [rows])
    .select();
  return { data, error };
}

/**
 * Met à jour un intervalle d'ouverture
 * @param {string} id
 * @param {{ jour_semaine?:number, heure_ouverture?:string, heure_fermeture?:string }} updates
 */
export async function updateHoraireOuverture(id, updates) {
  const { data, error } = await supabase
    .from('horaires_ouverture')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

/**
 * Supprime un intervalle d'ouverture
 * @param {string} id
 */
export async function deleteHoraireOuverture(id) {
  const { data, error } = await supabase
    .from('horaires_ouverture')
    .delete()
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

/**
 * Ajoute une compétence à un employé
 * @param {string} employeId - ID de l'employé
 * @param {string} competenceId - ID de la compétence
 * @returns {Promise<{data: object, error: object}>}
 */
export async function addEmployeCompetence(employeId, competenceId) {
  const { data, error } = await supabase
    .from('employes_competences')
    .insert([{ employe_id: employeId, competence_id: competenceId }])
    .select();

  return { data, error };
}

/**
 * Retire une compétence d'un employé
 * @param {string} employeId - ID de l'employé
 * @param {string} competenceId - ID de la compétence
 * @returns {Promise<{data: object, error: object}>}
 */
export async function removeEmployeCompetence(employeId, competenceId) {
  const { data, error } = await supabase
    .from('employes_competences')
    .delete()
    .eq('employe_id', employeId)
    .eq('competence_id', competenceId);

  return { data, error };
}

// ============================================================
// MATCHING AUTOMATIQUE
// ============================================================

/**
 * Trouve les employés qualifiés pour une prestation (possédant toutes les compétences requises)
 * @param {string} prestationId - ID de la prestation
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} siteId - ID du site
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getEmployesQualifies(prestationId, etablissementId, siteId) {
  // Récupérer les compétences requises par la prestation
  const { data: competencesRequises, error: errorCompetences } = await supabase
    .from('prestations_competences')
    .select('competence_id')
    .eq('prestation_id', prestationId);

  if (errorCompetences) return { data: null, error: errorCompetences };

  // Si aucune compétence requise, retourner tous les employés actifs
  if (!competencesRequises || competencesRequises.length === 0) {
    return getEmployes(etablissementId, siteId);
  }

  const competenceIds = competencesRequises.map((c) => c.competence_id);

  // Récupérer tous les employés actifs
  const { data: employes, error: errorEmployes } = await getEmployes(etablissementId, siteId);

  if (errorEmployes) return { data: null, error: errorEmployes };

  // Filtrer les employés qui ont TOUTES les compétences requises
  const employesQualifies = employes.filter((employe) => {
    const employeCompetenceIds = employe.competences?.map((ec) => ec.competence?.id) || [];
    return competenceIds.every((reqId) => employeCompetenceIds.includes(reqId));
  });

  return { data: employesQualifies, error: null };
}

/**
 * Trouve les ressources équipées pour une prestation (possédant toutes les compétences requises)
 * @param {string} prestationId - ID de la prestation
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} siteId - ID du site
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getRessourcesEquipees(prestationId, etablissementId, siteId) {
  // Récupérer les compétences requises par la prestation
  const { data: competencesRequises, error: errorCompetences } = await supabase
    .from('prestations_competences')
    .select('competence_id')
    .eq('prestation_id', prestationId);

  if (errorCompetences) return { data: null, error: errorCompetences };

  // Si aucune compétence requise, retourner toutes les ressources actives
  if (!competencesRequises || competencesRequises.length === 0) {
    return getRessourcesAll(etablissementId, siteId);
  }

  const competenceIds = competencesRequises.map((c) => c.competence_id);

  // Récupérer toutes les ressources actives
  const { data: ressources, error: errorRessources } = await getRessourcesAll(etablissementId, siteId);

  if (errorRessources) return { data: null, error: errorRessources };

  // Filtrer les ressources qui ont TOUTES les compétences requises
  const ressourcesEquipees = ressources.filter((ressource) => {
    const ressourceCompetenceIds = ressource.competences?.map((rc) => rc.competence?.id) || [];
    return competenceIds.every((reqId) => ressourceCompetenceIds.includes(reqId));
  });

  return { data: ressourcesEquipees, error: null };
}

/**
 * Vérifie si un employé est qualifié pour une prestation et retourne les compétences manquantes
 * @param {string} employeId - ID de l'employé
 * @param {string} prestationId - ID de la prestation
 * @returns {Promise<{qualifie: boolean, competencesManquantes: Array, error: object}>}
 */
export async function verifierQualificationEmploye(employeId, prestationId) {
  // Récupérer les compétences requises
  const { data: competencesRequises, error: errorRequises } = await supabase
    .from('prestations_competences')
    .select('competence:competence_id(id, libelle)')
    .eq('prestation_id', prestationId);

  if (errorRequises) return { qualifie: false, competencesManquantes: [], error: errorRequises };

  // Si aucune compétence requise, l'employé est qualifié
  if (!competencesRequises || competencesRequises.length === 0) {
    return { qualifie: true, competencesManquantes: [], error: null };
  }

  // Récupérer les compétences de l'employé
  const { data: competencesEmploye, error: errorEmploye } = await supabase
    .from('employes_competences')
    .select('competence:competence_id(id, libelle)')
    .eq('employe_id', employeId);

  if (errorEmploye) return { qualifie: false, competencesManquantes: [], error: errorEmploye };

  const employeCompetenceIds = competencesEmploye?.map((ec) => ec.competence.id) || [];

  // Trouver les compétences manquantes
  const competencesManquantes = competencesRequises
    .filter((cr) => !employeCompetenceIds.includes(cr.competence.id))
    .map((cr) => cr.competence);

  return {
    qualifie: competencesManquantes.length === 0,
    competencesManquantes,
    error: null,
  };
}

// ============================================================
// PARAMETRES
// ============================================================

/**
 * Récupère tous les paramètres d'un établissement/site
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} [siteId] - ID du site (optionnel)
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getParametres(etablissementId, _siteId = null) {
  const query = supabase
    .from('parametres')
    .select('*, site:site_id(id, nom)')
    .eq('etablissement_id', etablissementId)
    .order('cle');

  // Simplification : on récupère TOUS les paramètres de l'établissement
  // Le filtrage (Global vs Site) se fera côté client si nécessaire pour éviter les erreurs de requête complexe
  /*
  if (siteId) {
    query = query.or(`site_id.eq.${siteId},site_id.is.null`);
  }
  */ 

  const { data, error } = await query;
  return { data, error };
}

/**
 * Récupère un paramètre spécifique par sa clé
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} [siteId] - ID du site (optionnel, null pour paramètre établissement)
 * @param {string} cle - Clé du paramètre
 * @returns {Promise<{data: object, error: object}>}
 */
export async function getParametre(etablissementId, siteId, cle) {
  let query = supabase
    .from('parametres')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .eq('cle', cle);

  if (siteId) {
    query = query.eq('site_id', siteId);
  } else {
    query = query.is('site_id', null);
  }

  const { data, error } = await query.maybeSingle();
  return { data, error };
}

/**
 * Crée un nouveau paramètre
 * @param {object} parametreData - Données du paramètre
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createParametre(parametreData) {
  const { data, error } = await supabase
    .from('parametres')
    .insert([parametreData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour un paramètre
 * @param {string} id - ID du paramètre
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateParametre(id, updates) {
  const { data, error } = await supabase
    .from('parametres')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime un paramètre
 * @param {string} id - ID du paramètre
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteParametre(id) {
  const { data, error } = await supabase
    .from('parametres')
    .delete()
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Génère des créneaux en masse via une edge function
 * @param {object} params
 * @param {string} params.etablissementId
 * @param {string} params.siteId
 * @param {string} params.dateDebut
 * @param {string} params.dateFin
 * @param {string} params.heureDebut
 * @param {string} params.heureFin
 * @param {number} params.dureeCreneau
 * @param {string[]} params.employeIds
 * @returns {Promise<{data: object, error: object}>}
 */
export async function genererCreneaux(params) {
  let totalCount = 0;
  for (const employeId of params.employeIds) {
    const { data, error } = await supabase.rpc('generer_creneaux_recurrents', {
      p_etablissement_id: params.etablissementId,
      p_site_id: params.siteId,
      p_employe_id: employeId,
      p_ressource_id: null,
      p_date_debut: params.dateDebut,
      p_date_fin: params.dateFin,
      p_jours_semaine: [0, 1, 2, 3, 4, 5, 6], // Tous les jours pour l'instant
      p_heure_debut: params.heureDebut,
      p_heure_fin: params.heureFin,
      p_duree_creneau_min: params.dureeCreneau,
      p_pause_entre_creneaux_min: 0,
    });

    if (error) {
      return { data: null, error };
    }
    totalCount += data;
  }

  return { data: { count: totalCount }, error: null };
}

/**
 * Importe un fichier CSV de clients via une edge function
 * @param {File} csvFile - Fichier CSV à importer
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: object, error: object}>}
 */
export async function importerClientsBatch(clients, etablissementId) {
  const { data, error } = await invokeEdgeFunction('importer-clients', {
    body: { 
      clients,
      etablissementId 
    },
  }, { component: 'admin-clients', action: 'importerClientsBatch' });

  if (error) {
    return { data: null, error: { message: error.message } };
  }
  return { data, error: null };
}

/**
 * Déplace une réservation via l'edge function
 * @param {string} reservationId - ID de la réservation
 * @param {string} newEmployeId - ID du nouvel employé
 * @param {string} newDebut - Nouvelle date de début (ISO string)
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deplacerReservation(reservationId, newEmployeId, newDebut, source = 'public') {
  let headers = undefined;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (token) headers = { Authorization: `Bearer ${token}` };
  } catch {}

  const { data, error } = await invokeEdgeFunction('deplacer-reservation', {
    body: { 
      reservation_id: reservationId,
      new_employe_id: newEmployeId,
      new_debut: newDebut,
      source
    },
    headers,
  }, { component: 'planning', action: 'deplacerReservation' });

  if (error) {
    return { data: null, error: new Error(error.message || 'Erreur de communication avec la fonction.') };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || "Erreur lors du déplacement") };
}

/**
 * Intervertit deux réservations de manière atomique via l'edge function.
 * @param {string} reservationIdA - ID de la première réservation
 * @param {string} reservationIdB - ID de la seconde réservation
 * @param {string} source - Source de l'action (admin/public)
 * @returns {Promise<{data: object, error: object}>}
 */
export async function intervertirReservations(reservationIdA, reservationIdB, source = 'admin') {
  let headers = undefined;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (token) headers = { Authorization: `Bearer ${token}` };
  } catch {}

  const { data, error } = await invokeEdgeFunction('deplacer-reservation', {
    body: {
      action: 'swap',
      reservation_id_1: reservationIdA,
      reservation_id_2: reservationIdB,
      source,
    },
    headers,
  }, { component: 'planning', action: 'intervertirReservations' });

  if (error) {
    return { data: null, error: new Error(error.message || 'Erreur de communication avec la fonction.') };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || "Erreur lors de l'interversion") };
}

/**
 * Supprime tous les clients d'un établissement via une edge function
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteAllClients(etablissementId) {
  const { data, error } = await invokeEdgeFunction('supprimer-tous-les-clients', {
    body: { etablissement_id: etablissementId },
  }, { component: 'admin-clients', action: 'deleteAllClients' });

  if (error) {
    return { data: null, error };
  }

  if (data?.success) {
    return { data: data.data, error: null };
  }

  return { data: null, error: new Error(data?.error || 'Erreur lors de la suppression des clients') };
}

// ============================================================================
// FONCTIONS SUIVI SMS BREVO (admin)
// ============================================================================

function normalizeBrevoMonitoringError(rawError, fallbackMessage) {
  const err = new Error(rawError?.message || fallbackMessage);
  if (rawError?.code) err.code = rawError.code;
  if (rawError?.details) err.details = rawError.details;
  return err;
}

async function invokeBrevoSmsMonitoring(action, params = {}, logOptions = {}) {
  const body = { action, ...params };
  const { data, error } = await invokeEdgeFunction(
    'brevo-sms-monitoring',
    { body },
    { component: 'admin-sms-monitoring', action, ...logOptions },
  );

  if (error) {
    return {
      data: null,
      error: normalizeBrevoMonitoringError(error, "Erreur lors de l'appel Brevo SMS monitoring"),
    };
  }

  if (data?.success) {
    return { data: data.data, meta: data.meta || null, error: null };
  }

  const appError = data?.error || null;
  return {
    data: null,
    error: normalizeBrevoMonitoringError(appError, 'Erreur Brevo SMS monitoring'),
  };
}

export async function getBrevoSmsSummary(params = {}) {
  const { data, meta, error } = await invokeBrevoSmsMonitoring('summary', params);
  return { data, meta, error };
}

export async function getBrevoSmsStats(params = {}) {
  const { data, meta, error } = await invokeBrevoSmsMonitoring('stats', params);
  return { data, meta, error };
}

export async function getBrevoSmsEvents(params = {}) {
  const { data, meta, error } = await invokeBrevoSmsMonitoring('events', params);
  return { data, meta, error };
}

export async function getBrevoSmsMessages(params = {}) {
  const { data, meta, error } = await invokeBrevoSmsMonitoring('messages', params);
  return { data, meta, error };
}

export async function getBrevoSmsWeeklyByTag(params = {}) {
  const { data, meta, error } = await invokeBrevoSmsMonitoring('weekly_by_tag', params);
  return { data, meta, error };
}

export async function getBrevoOrgActivities(params = {}) {
  const { data, meta, error } = await invokeBrevoSmsMonitoring('activities', params);
  return { data, meta, error };
}

const OTP_IDEMPOTENCY_TTL_MS = 5 * 60 * 1000;
const otpSmsIdempotencyCache = new Map();

function stableStringify(value) {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }

  const entries = Object.entries(value).sort(([a], [b]) => a.localeCompare(b));
  const serialized = entries.map(([key, entryValue]) => `${JSON.stringify(key)}:${stableStringify(entryValue)}`);
  return `{${serialized.join(',')}}`;
}

function cleanupOtpIdempotencyCache(nowTs) {
  for (const [fingerprint, cached] of otpSmsIdempotencyCache.entries()) {
    if (!cached || cached.expiresAt <= nowTs) {
      otpSmsIdempotencyCache.delete(fingerprint);
    }
  }
}

function getOtpIdempotencyKey(phone, payload) {
  const nowTs = Date.now();
  cleanupOtpIdempotencyCache(nowTs);

  const normalizedPhone = String(phone || '').trim();
  const fingerprint = `${normalizedPhone}::${stableStringify(payload ?? {})}`;
  const cached = otpSmsIdempotencyCache.get(fingerprint);

  if (cached && cached.expiresAt > nowTs) {
    return cached.key;
  }

  const randomId =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${nowTs.toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const key = `otp:${randomId}`;

  otpSmsIdempotencyCache.set(fingerprint, {
    key,
    expiresAt: nowTs + OTP_IDEMPOTENCY_TTL_MS,
  });

  return key;
}

// ============================================================================
// FONCTIONS SMS (réservation publique)
// ============================================================================

/**
 * Envoie un code SMS de vérification
 * @param {string} phone - Numéro de téléphone formaté
 * @param {object} payload - Données de la réservation à stocker
 * @returns {Promise<{data: object, error: object}>}
 */
export async function envoyerCodeSms(phone, payload, options = {}) {
  const idempotencyKey = getOtpIdempotencyKey(phone, payload);
  const { data, error } = await invokeEdgeFunction('envoyer-code-sms', {
    body: {
      phone,
      payload,
      idempotencyKey,
      captchaToken: options.captchaToken || null,
      clientFingerprint: options.clientFingerprint || null,
      context: options.context || undefined,
    },
    headers: { 'Idempotency-Key': idempotencyKey },
  }, { component: 'booking-wizard', action: 'envoyerCodeSms' });

  if (error) {
    const normalizedError = new Error(error.message || 'Erreur lors de l\'envoi du SMS');
    normalizedError.code = error.code || null;
    normalizedError.details = error.details || null;
    return { data: null, error: normalizedError };
  }

  // L'Edge Function retourne { success: true, token: "..." } ou { success: true, data: { token } }
  if (data?.success) {
    // Gérer les deux formats possibles
    const token = data.token || data.data?.token;
    return { data: { token, idempotencyKey }, error: null };
  }

  return { data: null, error: new Error(data?.error || 'Erreur lors de l\'envoi du SMS') };
}

/**
 * Vérifie un code SMS
 * @param {string} token - Token de vérification
 * @param {string} code - Code saisi par l'utilisateur
 * @returns {Promise<{data: object, error: object}>}
 */
export async function verifierCodeSms(token, code, options = {}) {
  const { data, error } = await invokeEdgeFunction('verifier-code-sms', {
    body: {
      token,
      code,
      clientFingerprint: options.clientFingerprint || null,
      context: options.context || undefined,
    },
  }, { component: 'booking-wizard', action: 'verifierCodeSms' });

  if (error) {
    const normalizedError = new Error(error.message || 'Erreur lors de la vérification du code');
    normalizedError.code = error.code || null;
    normalizedError.details = error.details || null;
    return { data: null, error: normalizedError };
  }

  if (data?.success) {
    // Si un token d'authentification est retourné, l'utiliser pour connecter l'utilisateur
    if (data.authToken?.token_hash) {
      try {
        const { supabase } = await import('./supabaseClient.js');
        const { setState } = await import('./store.js');
        
        // Vérifier le token OTP pour créer une session
        const { data: sessionData, error: sessionError } = await supabase.auth.verifyOtp({
          token_hash: data.authToken.token_hash,
          type: 'magiclink',
        });
        
        if (sessionError) {
          console.warn('Erreur verifyOtp:', sessionError.message);
        } else if (sessionData?.session) {
          // Mettre à jour le store
          setState({
            isAuthenticated: true,
            user: sessionData.session.user,
          });
          
          console.warn('✅ Utilisateur connecté automatiquement après vérification SMS');
        }
      } catch (sessionError) {
        // Ne pas bloquer si la connexion automatique échoue
        console.warn('Connexion automatique après SMS échouée:', sessionError);
      }
    }
    
    return { data: data, error: null };
  }

  return { data: null, error: new Error(data?.error || 'Erreur lors de la vérification du code') };
}

/**
 * Met à jour les informations utilisateur lors de la connexion (fire-and-forget)
 * @returns {Promise<{data: object, error: object}>}
 */
export async function upsertUtilisateurLogin() {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      return { data: null, error: null };
    }
  } catch {
    return { data: null, error: null };
  }

  const { data, error } = await invokeEdgeFunction(
    'upsert-utilisateur-login',
    {},
    { component: 'auth', action: 'upsertUtilisateurLogin' }
  );

  return { data, error };
}

// ============================================================================
// LOGS D'ERREURS
// ============================================================================

// Variable pour stocker le contexte global (établissement/site)
const errorLogContext = {
  etablissement_id: null,
  site_id: null,
};

/**
 * Met à jour le contexte global pour les logs d'erreurs
 * À appeler après connexion ou changement d'établissement/site
 */
export function setErrorLogContext(context = {}) {
  if (context.etablissementId !== undefined) {
    errorLogContext.etablissement_id = context.etablissementId;
  }
  if (context.siteId !== undefined) {
    errorLogContext.site_id = context.siteId;
  }
}

/**
 * Wrapper pour les appels aux Edge Functions avec logging automatique des erreurs
 * @param {string} functionName - Nom de la fonction Edge
 * @param {object} options - Options pour supabase.functions.invoke
 * @param {object} [logOptions] - Options pour le logging
 * @param {string} [logOptions.action] - Action descriptive (défaut: functionName)
 * @param {string} [logOptions.component] - Composant source
 * @returns {Promise<{data: any, error: any}>}
 */
async function invokeEdgeFunction(functionName, options = {}, logOptions = {}) {
  try {
    const { data, error } = await supabase.functions.invoke(functionName, options);

    if (error) {
      // Extraire le message d'erreur détaillé si disponible
      let errorMessage = error.message || 'Erreur Edge Function';
      const errorContext = { functionName, requestBody: options.body };

      // Tenter d'extraire le corps de l'erreur HTTP
      if (error.name === 'FunctionsHttpError' && error.context) {
        try {
          const errorBody = await error.context.json();
          if (errorBody?.error) {
            const normalizedError = typeof errorBody.error === 'string'
              ? { message: errorBody.error }
              : errorBody.error;
            errorMessage = normalizedError?.message || JSON.stringify(normalizedError);
            errorContext.responseBody = errorBody;
            if (normalizedError?.code) error.code = normalizedError.code;
            if (normalizedError?.details) error.details = normalizedError.details;
            // Propager le message détaillé au caller (sinon on n'a que "non-2xx")
            try {
              error.message = errorMessage;
            } catch {
              // Ignorer si l'objet error est en lecture seule
            }
          }
        } catch {
          // Ignorer si on ne peut pas parser le JSON
        }
      }

      // Logger l'erreur dans la base de données
      await logErrorInternal({
        message: `Edge Function "${functionName}": ${errorMessage}`,
        level: 'error',
        code: `EDGE_FUNCTION_${functionName.toUpperCase().replace(/-/g, '_')}`,
        component: logOptions.component || 'edge-function',
        action: logOptions.action || functionName,
        context: errorContext,
        etablissement_id: logOptions.etablissement_id || errorLogContext.etablissement_id,
        site_id: logOptions.site_id || errorLogContext.site_id,
      });

      return { data: null, error };
    }

    // Vérifier si la réponse contient une erreur applicative
    if (data && data.success === false && data.error) {
      await logErrorInternal({
        message: `Edge Function "${functionName}": ${data.error}`,
        level: 'error',
        code: `EDGE_FUNCTION_${functionName.toUpperCase().replace(/-/g, '_')}`,
        component: logOptions.component || 'edge-function',
        action: logOptions.action || functionName,
        context: { functionName, requestBody: options.body, responseData: data },
        etablissement_id: logOptions.etablissement_id || errorLogContext.etablissement_id,
        site_id: logOptions.site_id || errorLogContext.site_id,
      });
    }

    return { data, error: null };
  } catch (e) {
    // Erreur inattendue (réseau, etc.)
    const errorMessage = e instanceof Error ? e.message : String(e);
    
    await logErrorInternal({
      message: `Edge Function "${functionName}": ${errorMessage}`,
      level: 'critical',
      code: `EDGE_FUNCTION_${functionName.toUpperCase().replace(/-/g, '_')}_CRASH`,
      component: logOptions.component || 'edge-function',
      action: logOptions.action || functionName,
      stack_trace: e instanceof Error ? e.stack : null,
      context: { functionName, requestBody: options.body },
      etablissement_id: logOptions.etablissement_id || errorLogContext.etablissement_id,
      site_id: logOptions.site_id || errorLogContext.site_id,
    });

    return { data: null, error: { message: errorMessage } };
  }
}

/**
 * Version interne de logError pour éviter les dépendances circulaires
 * @private
 */
async function logErrorInternal(errorData) {
  if (!errorData?.message) return { data: null, error: null };

  let userId = null;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session?.user?.id) {
      const { data: userData } = await supabase
        .from('utilisateurs')
        .select('id')
        .eq('auth_user_id', sessionData.session.user.id)
        .single();
      userId = userData?.id || null;
    }
  } catch {
    // Ignorer
  }

  const logEntry = {
    message: errorData.message,
    level: errorData.level || 'error',
    code: errorData.code || null,
    component: errorData.component || null,
    action: errorData.action || null,
    stack_trace: errorData.stack_trace || null,
    context: errorData.context || null,
    etablissement_id: errorData.etablissement_id || null,
    site_id: errorData.site_id || null,
    user_id: userId,
    url: typeof window !== 'undefined' ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  };

  try {
    const { data, error } = await supabase
      .from('app_error_logs')
      .insert(logEntry)
      .select()
      .single();
    
    if (error) {
      console.error('🔴 Impossible de logger l\'erreur:', error);
    }
    return { data, error };
  } catch (e) {
    console.error('🔴 Erreur lors du logging:', e);
    return { data: null, error: e };
  }
}

/**
 * Enregistre une erreur bloquante dans la base de données
 * @param {object} errorData - Données de l'erreur
 * @param {string} errorData.message - Message d'erreur (obligatoire)
 * @param {string} [errorData.level] - Niveau: 'error', 'critical', 'fatal' (défaut: 'error')
 * @param {string} [errorData.code] - Code d'erreur applicatif
 * @param {string} [errorData.component] - Composant source
 * @param {string} [errorData.action] - Action en cours
 * @param {string} [errorData.stack_trace] - Stack trace
 * @param {object} [errorData.context] - Données contextuelles
 * @param {string} [errorData.etablissement_id] - ID de l'établissement
 * @param {string} [errorData.site_id] - ID du site
 * @returns {Promise<{data: object, error: object}>}
 */
export async function logError(errorData) {
  if (!errorData?.message) {
    return { data: null, error: { message: 'Le message d\'erreur est obligatoire' } };
  }

  // Récupérer l'utilisateur connecté si disponible
  let userId = null;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session?.user?.id) {
      // Chercher l'utilisateur dans la table utilisateurs
      const { data: userData } = await supabase
        .from('utilisateurs')
        .select('id')
        .eq('auth_user_id', sessionData.session.user.id)
        .single();
      userId = userData?.id || null;
    }
  } catch {
    // Ignorer les erreurs d'authentification
  }

  const logEntry = {
    message: errorData.message,
    level: errorData.level || 'error',
    code: errorData.code || null,
    component: errorData.component || null,
    action: errorData.action || null,
    stack_trace: errorData.stack_trace || null,
    context: errorData.context || null,
    etablissement_id: errorData.etablissement_id || null,
    site_id: errorData.site_id || null,
    user_id: userId,
    url: typeof window !== 'undefined' ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  };

  const { data, error } = await supabase
    .from('app_error_logs')
    .insert(logEntry)
    .select()
    .single();

  return { data, error };
}

/**
 * Récupère les logs d'erreurs d'un établissement
 * @param {string} etablissementId - ID de l'établissement
 * @param {object} [options] - Options de filtrage
 * @param {string} [options.level] - Filtrer par niveau
 * @param {boolean} [options.unresolvedOnly] - Uniquement les non résolues
 * @param {number} [options.limit] - Nombre max de résultats (défaut: 100)
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getErrorLogs(etablissementId, options = {}) {
  let query = supabase
    .from('app_error_logs')
    .select(`
      *,
      user:user_id (id, email),
      resolved_by_user:resolved_by (id, email)
    `)
    .order('created_at', { ascending: false })
    .limit(options.limit || 100);

  if (etablissementId) {
    query = query.eq('etablissement_id', etablissementId);
  }

  if (options.level) {
    query = query.eq('level', options.level);
  }

  if (options.unresolvedOnly) {
    query = query.is('resolved_at', null);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Marque une erreur comme résolue
 * @param {string} errorId - ID de l'erreur
 * @param {string} [notes] - Notes de résolution
 * @returns {Promise<{data: object, error: object}>}
 */
export async function resolveError(errorId, notes = null) {
  // Récupérer l'utilisateur connecté
  let resolvedBy = null;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session?.user?.id) {
      const { data: userData } = await supabase
        .from('utilisateurs')
        .select('id')
        .eq('auth_user_id', sessionData.session.user.id)
        .single();
      resolvedBy = userData?.id || null;
    }
  } catch {
    // Ignorer
  }

  const { data, error } = await supabase
    .from('app_error_logs')
    .update({
      resolved_at: new Date().toISOString(),
      resolved_by: resolvedBy,
      resolution_notes: notes,
    })
    .eq('id', errorId)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime les anciens logs d'erreurs (plus de X jours)
 * @param {number} daysOld - Nombre de jours (défaut: 90)
 * @returns {Promise<{data: object, error: object}>}
 */
export async function purgeOldErrorLogs(daysOld = 90) {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysOld);

  const { data, error } = await supabase
    .from('app_error_logs')
    .delete()
    .lt('created_at', cutoffDate.toISOString())
    .not('resolved_at', 'is', null); // Ne supprimer que les erreurs résolues

  return { data, error };
}

// ===== FONCTIONS CARROUSEL BANNIÈRE =====

/**
 * Récupère les images du carrousel pour un établissement
 * @param {string} etablissementId - ID de l'établissement
 * @param {boolean} [activeOnly=false] - Filtrer uniquement les images actives
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getCarouselImages(etablissementId, activeOnly = false) {
  let query = supabase
    .from('carousel_images')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .order('ordre');

  if (activeOnly) {
    query = query.eq('actif', true);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Récupère les images actives du carrousel pour l'affichage public
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getCarouselImagesPublic(etablissementId) {
  const { data, error } = await supabase
    .from('carousel_images')
    .select('id, titre, description, image_desktop_url, image_mobile_url, lien_url, ordre')
    .eq('etablissement_id', etablissementId)
    .eq('actif', true)
    .order('ordre');

  return { data, error };
}

/**
 * Crée une nouvelle image dans le carrousel
 * @param {object} imageData - Données de l'image
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createCarouselImage(imageData) {
  const { data, error } = await supabase
    .from('carousel_images')
    .insert([imageData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour une image du carrousel
 * @param {string} id - ID de l'image
 * @param {object} updates - Données à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateCarouselImage(id, updates) {
  const { data, error } = await supabase
    .from('carousel_images')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime une image du carrousel
 * @param {string} id - ID de l'image
 * @returns {Promise<{data: object, error: object}>}
 */
export async function deleteCarouselImage(id) {
  const { data, error } = await supabase
    .from('carousel_images')
    .delete()
    .eq('id', id);

  return { data, error };
}

/**
 * Met à jour l'ordre des images du carrousel
 * @param {Array<{id: string, ordre: number}>} ordres - Liste des IDs avec leur nouvel ordre
 * @returns {Promise<{error: object}>}
 */
export async function updateCarouselOrdre(ordres) {
  const promises = ordres.map(({ id, ordre }) =>
    supabase
      .from('carousel_images')
      .update({ ordre, updated_at: new Date().toISOString() })
      .eq('id', id)
  );

  const results = await Promise.all(promises);
  const error = results.find((r) => r.error)?.error || null;
  return { error };
}

/**
 * Upload une image vers le bucket carousel
 * @param {File} file - Fichier image
 * @param {string} etablissementId - ID de l'établissement
 * @param {string} type - Type d'image ('desktop' ou 'mobile')
 * @returns {Promise<{url: string|null, error: object}>}
 */
export async function uploadCarouselImage(file, etablissementId, type) {
  const ext = file.name.split('.').pop();
  const fileName = `${etablissementId}/${type}_${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('carousel')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    return { url: null, error: uploadError };
  }

  const { data: urlData } = supabase.storage
    .from('carousel')
    .getPublicUrl(fileName);

  return { url: urlData?.publicUrl || null, error: null };
}

/**
 * Supprime une image du bucket carousel
 * @param {string} url - URL publique de l'image
 * @returns {Promise<{error: object}>}
 */
export async function deleteCarouselImageFile(url) {
  if (!url) return { error: null };

  // Extraire le chemin du fichier depuis l'URL
  const match = url.match(/\/carousel\/(.+)$/);
  if (!match) return { error: null };

  const filePath = match[1];
  const { error } = await supabase.storage
    .from('carousel')
    .remove([filePath]);

  return { error };
}

// ============================================================
// JOURNAL DES ACTIONS
// ============================================================

/**
 * Enregistre une action dans le journal
 * @param {object} params - Paramètres de l'action
 * @param {string} params.etablissementId - ID de l'établissement
 * @param {string} [params.siteId] - ID du site (optionnel)
 * @param {string} params.entityType - Type d'entité (ex: 'client', 'reservation')
 * @param {string} [params.entityId] - ID de l'entité concernée
 * @param {string} params.action - Action effectuée (ex: 'update', 'create', 'delete')
 * @param {string} [params.description] - Description lisible de l'action
 * @param {object} [params.payloadBefore] - État avant modification
 * @param {object} [params.payloadAfter] - État après modification
 * @param {object} [params.metadata] - Métadonnées supplémentaires
 * @param {string} [params.performedBy] - ID de l'utilisateur qui effectue l'action
 * @returns {Promise<{data: object, error: object}>}
 */
export async function logAction({
  etablissementId,
  siteId = null,
  entityType,
  entityId = null,
  action,
  description = null,
  payloadBefore = null,
  payloadAfter = null,
  metadata = null,
  performedBy = null,
}) {
  const { data, error } = await supabase
    .from('journal_actions_admin')
    .insert([{
      etablissement_id: etablissementId,
      site_id: siteId,
      entity_type: entityType,
      entity_id: entityId,
      action,
      description,
      payload_before: payloadBefore,
      payload_after: payloadAfter,
      metadata,
      performed_by: performedBy,
    }])
    .select()
    .single();

  if (error) {
    console.warn('Erreur journalisation action:', error);
  }

  return { data, error };
}

// ============================================================
// FAQ (Questions fréquentes)
// ============================================================

/**
 * Récupère toutes les FAQ d'un établissement
 * @param {string} etablissementId - ID de l'établissement
 * @param {boolean} [activeOnly=false] - Si true, ne retourne que les FAQ actives
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getFaq(etablissementId, activeOnly = false) {
  let query = supabase
    .from('faq')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .order('categorie')
    .order('ordre');

  if (activeOnly) {
    query = query.eq('actif', true);
  }

  const { data, error } = await query;
  return { data, error };
}

/**
 * Récupère les catégories distinctes de FAQ
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: string[], error: object}>}
 */
export async function getFaqCategories(etablissementId) {
  const { data, error } = await supabase
    .from('faq')
    .select('categorie')
    .eq('etablissement_id', etablissementId)
    .order('categorie');

  if (error) return { data: [], error };

  // Extraire les catégories uniques
  const categories = [...new Set(data.map((d) => d.categorie))];
  return { data: categories, error: null };
}

/**
 * Crée une nouvelle FAQ
 * @param {object} faqData - Données de la FAQ
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createFaq(faqData) {
  const { data, error } = await supabase
    .from('faq')
    .insert([faqData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour une FAQ existante
 * @param {string} id - ID de la FAQ
 * @param {object} updates - Champs à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateFaq(id, updates) {
  const { data, error } = await supabase
    .from('faq')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime une FAQ
 * @param {string} id - ID de la FAQ
 * @returns {Promise<{error: object}>}
 */
export async function deleteFaq(id) {
  const { error } = await supabase
    .from('faq')
    .delete()
    .eq('id', id);

  return { error };
}

/**
 * Met à jour l'ordre des FAQ
 * @param {Array<{id: string, ordre: number}>} items - Liste des FAQ avec leur nouvel ordre
 * @returns {Promise<{error: object}>}
 */
export async function updateFaqOrdre(items) {
  const promises = items.map(({ id, ordre }) =>
    supabase.from('faq').update({ ordre }).eq('id', id)
  );

  const results = await Promise.all(promises);
  const error = results.find((r) => r.error)?.error || null;
  return { error };
}

// ============================================================================
// ÉTABLISSEMENTS
// ============================================================================

/**
 * Récupère un établissement par son ID
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: object, error: object}>}
 */
export async function getEtablissement(etablissementId) {
  if (!etablissementId) {
    return { data: null, error: { message: 'ID établissement requis' } };
  }

  const { data, error } = await supabase
    .from('etablissements')
    .select('*')
    .eq('id', etablissementId)
    .maybeSingle();

  return { data, error };
}

// ============================================================================
// SITES
// ============================================================================

/**
 * Récupère tous les sites d'un établissement
 * @param {string} etablissementId - ID de l'établissement
 * @returns {Promise<{data: Array, error: object}>}
 */
export async function getSites(etablissementId) {
  const { data, error } = await supabase
    .from('sites')
    .select('*')
    .eq('etablissement_id', etablissementId)
    .order('nom');

  return { data, error };
}

/**
 * Récupère un site par son ID
 * @param {string} siteId - ID du site
 * @returns {Promise<{data: object, error: object}>}
 */
export async function getSite(siteId) {
  const { data, error } = await supabase
    .from('sites')
    .select('*')
    .eq('id', siteId)
    .single();

  return { data, error };
}


/**
 * Recupere les offres actives pour un site et une date de reference.
 * @param {string} siteId - ID du site
 * @param {string|Date} [refDate] - Date de reference (defaut: aujourd'hui)
 * @returns {Promise<{data: Array|null, error: object|null}>}
 */
export async function getOffresActives(siteId, refDate = null) {
  if (!siteId) return { data: [], error: null };

  const params = { p_site_id: siteId };
  if (refDate) {
    const ref = refDate instanceof Date ? refDate : new Date(refDate);
    if (!Number.isNaN(ref.getTime())) {
      const yyyy = ref.getFullYear();
      const mm = String(ref.getMonth() + 1).padStart(2, '0');
      const dd = String(ref.getDate()).padStart(2, '0');
      params.p_ref_date = `${yyyy}-${mm}-${dd}`;
    }
  }

  const { data, error } = await supabase.rpc('get_offres_actives', params);
  return { data: data || [], error };
}

/**
 * Recupere la PREMIERE offre active (legacy, pour compatibilité).
 * Recommande d'utiliser getOffresActives à la place.
 * @param {string} siteId - ID du site
 * @param {string|Date} [refDate] - Date de reference (defaut: aujourd'hui)
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function getOffreActive(siteId, refDate = null) {
  const { data, error } = await getOffresActives(siteId, refDate);
  if (error) return { data: null, error };
  return { data: data?.[0] || null, error: null };
}

/**
 * Recupere les offres d'un site (admin).
 * @param {string} siteId - ID du site
 * @returns {Promise<{data: Array, error: object|null}>}
 */
export async function getOffres(siteId) {
  if (!siteId) return { data: [], error: null };
  const { data, error } = await supabase
    .from('offres')
    .select('*')
    .eq('site_id', siteId)
    .order('actif', { ascending: false })
    .order('date_debut', { ascending: false });
  return { data: data || [], error };
}

/**
 * Recupere les types d'offre (admin).
 * @returns {Promise<{data: Array, error: object|null}>}
 */
export async function getTypesOffre() {
  const { data, error } = await supabase
    .from('types_offre')
    .select('code, libelle, actif')
    .order('code', { ascending: true });
  return { data: data || [], error };
}

/**
 * Cree une offre.
 * @param {object} offreData
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function createOffre(offreData) {
  const { data, error } = await supabase
    .from('offres')
    .insert([offreData])
    .select()
    .single();
  return { data, error };
}

/**
 * Met a jour une offre.
 * @param {string} id
 * @param {object} updates
 * @returns {Promise<{data: object|null, error: object|null}>}
 */
export async function updateOffre(id, updates) {
  const { data, error } = await supabase
    .from('offres')
    .update({ ...updates })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

/**
 * Supprime une offre.
 * @param {string} id
 * @returns {Promise<{error: object|null}>}
 */
export async function deleteOffre(id) {
  const { error } = await supabase
    .from('offres')
    .delete()
    .eq('id', id);
  return { error };
}


/**
 * Crée un nouveau site
 * @param {object} siteData - Données du site
 * @returns {Promise<{data: object, error: object}>}
 */
export async function createSite(siteData) {
  const { data, error } = await supabase
    .from('sites')
    .insert([siteData])
    .select()
    .single();

  return { data, error };
}

/**
 * Met à jour un site
 * @param {string} id - ID du site
 * @param {object} updates - Champs à mettre à jour
 * @returns {Promise<{data: object, error: object}>}
 */
export async function updateSite(id, updates) {
  const { data, error } = await supabase
    .from('sites')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  return { data, error };
}

/**
 * Supprime un site
 * @param {string} id - ID du site
 * @returns {Promise<{error: object}>}
 */
export async function deleteSite(id) {
  const { error } = await supabase
    .from('sites')
    .delete()
    .eq('id', id);

  return { error };
}
